
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
    Select,
    Menu,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import { Close as CloseIcon } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import CustomDropdown from "../SizesColorsDropdown";
import CustomTextField from "../../CustomTextField";
import {
  formatMinimumTwoDecimalPlaces,
  normalizeEditableDecimalForCommit,
} from "../pricingDisplay";
import {
  buildDecorationCopyPayload,
  resolveMatrixChargeTypesForCopy,
  findMatchingDecorationLocations,
  getCartProductCopyKey,
  getDecorationLocation,
  getProductDecorations,
  isSameCartProduct,
} from "./copyHelpers";
import styles from "./index.module.css"; // Import CSS module
import PricingTypeSelect from "../PricingTypeDropdown";
import DecorationSelectorPopup from "../DecorationSelectorPopup";
import { getSingleDecoratorDetails } from "../../../services/decorator";
import ArtworkUploadModal from "../AddArtWorkPopup";
import GridOnIcon from '@mui/icons-material/GridOn';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { isEliteChargeNameReadOnly } from './eliteChargeNameEditing';
import { applyMatrixChargeType, isFixedChargeRow } from './matrixChargeType';
import { resolvePopupRowData } from './popupRowBinding';
import { Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import {
  DISPLAY_STATUS,
  DISPLAY_STATUS_OPTIONS,
  normalizeDisplayStatus,
} from "../../../utils/pricingOptions";
import { getArtworkPreviewUrl } from "../artworkFileUtils";
import decorationPricing from "./pricingHelpers";
import {
  getDecorationArtworkChoices,
  shouldShowInlineArtworkAddControl,
} from "./artworkLayoutHelpers";

import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";

// Point to the ESM worker file using a URL string CRA/Vite both understand
GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

// ——— put near top-level helpers ———
const getExt = (url = "") => {
  const m = String(url).match(/\.([a-z0-9]+)(?:\?|#|$)/i);
  return m ? m[1].toUpperCase() : "FILE";
};
const IMG_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
const getFileName = (url="") => {
  try {
    const u = new URL(url, window.location.origin);
    return decodeURIComponent(u.pathname.split("/").pop() || "File");
  } catch {
    const parts = String(url).split(/[\/?#]/);
    return decodeURIComponent(parts.pop() || "File");
  }
}
// track broken images so we can fall back to icon

const options = DISPLAY_STATUS_OPTIONS.INTERNATIONAL;

const PROMOSTORE_SELECTION_MODES = [
  { id: "included", name: "Applies to every sale (automatically add artwork)" },
  { id: "optional", name: "Optional customer choice" },
  { id: "required_choice", name: "Required customer choice" },
  { id: "optional_choice", name: "Optional grouped choice" },
  { id: "internal", name: "Internal / not customer selectable" },
];

const normalizePromoStoreSelectionMode = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase().replace(/[\s_-]+/g, "_");
  return PROMOSTORE_SELECTION_MODES.some((option) => option.id === normalized)
    ? normalized
    : "";
};

const requiresPromoStoreSelectionGroup = (mode) =>
  ["required_choice", "optional_choice"].includes(normalizePromoStoreSelectionMode(mode));

const optionsForNormalCost = [
  ...DISPLAY_STATUS_OPTIONS.STANDARD,
  { id: DISPLAY_STATUS.SUBTRACT_FROM_MARGIN, name: DISPLAY_STATUS.SUBTRACT_FROM_MARGIN },
];

// allow: "", "42", "42.", ".5", "0.75"
const DECIMAL_RX = /^(\d+(\.\d*)?|\.\d*)?$/;
// allow only digits (for qty)
const INTEGER_RX = /^\d*$/;

const as2 = (v) => (Number.isFinite(parseFloat(v)) ? parseFloat(v).toFixed(2) : "");
const asI = (v) => (Number.isFinite(parseInt(v))   ? String(parseInt(v))     : "");
const toNum = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};
const {
  deriveRetailFromRow: deriveDecorationRetailFromRow,
  resolveRetailFromInputs: resolveDecorationRetailFromInputs,
  applyRetailEditToDecorationRow,
  hasConvertedCostBasis,
  getEffectiveDecorationCost: getEffectiveDecorationCostFromRow,
  calculateDefaultDecorationQuantity,
  syncInitialDecorationRunChargeQuantities,
} = decorationPricing;
const isLockedBool = (value) => value === true || String(value).toLowerCase() === "true";
const SUPPLIER_UNSPEC = "supplier|$UNI";
const EXTERNAL_DECORATORS = [
  { id: 18, label: "Merit Embroidery" },
    // ── New 3rd-party decorators ─────────────────────────────────────────────
  {
    id: 19,
    label: "First Class Engraving",
    address: "115 Hawkhill Way NW",
    website: "https://www.firstclasstrophyshop.ca/",
    contacts: [
      { name: "Randy Annie", email: "firstclassengraving@hotmail.com" },
    ],
    aliases: ["First Class Trophy Shop", "First Class"],
  },
  {
    id: 20,
    label: "Iconic Embroidery",
    address: "3800 19 St NE Bay 4, Calgary, AB T2E 6V2",
    website: "https://iconicembroidery.com/",
    contacts: [
      { name: "Robinson Rodrigues", email: "robbie@iconicembroidery.com" },
      { name: "Jordan Piraux", email: "jordan@iconicembroidery.com" },
      { name: "Safiya Fraser", email: "safiya@iconicembroidery.com" },
      { name: "Kyle Kuchma", email: "kyle.kuchma@iconicembroidery.com" },
    ],
    aliases: ["Iconic"],
  },
  {
    id: 21,
    label: "Fast Forward Printing",
    address: "3740 11a St NE, Calgary, AB T2E 6M6",
    website: "https://www.ffwdprinting.ca/",
    contacts: [
      { name: "Gord Salt", email: "gord@ffwdprinting.ca" },
      { name: "Artwork Department", email: "art@ffwdprinting.ca" },
    ],
    aliases: ["fast forward", "FFWD Printing", "FFWD"],
  },
  {
    id: 22,
    label: "United Stitch",
    address: "2410E 2 Ave SE, Calgary, AB T2E 6J9",
    website: "http://www.saveonstitch.com/sos/",
    contacts: [
      { name: "Sales Orders", email: "calgaryinfo@saveonstitch.com" },
    ],
    aliases: ["Save On Stitch", "Save-On-Stitch"],
  },
];
// simple in-memory cache so we don’t re-render thumbs repeatedly
const pdfThumbCache = new Map(); // key: src -> dataURL

const PdfThumb = ({
  src,
  width = 200,
  height = 200,
  skeleton = "loading…",
  onStart,
  onDone,
  onError,
}) => {
  const [dataUrl, setDataUrl] = useState(() => (src ? pdfThumbCache.get(src) || "" : ""));
  const [failed, setFailed] = useState(false);

  // reset state whenever src changes
  useEffect(() => {
    setFailed(false);
    setDataUrl(src ? pdfThumbCache.get(src) || "" : "");
  }, [src]);

  useEffect(() => {
    let cancelled = false;
    if (!src) return;
    if (dataUrl) return; // already cached / rendered

    onStart && onStart();

    (async () => {
      try {
        const loadingTask = getDocument({ url: src, withCredentials: false });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const v0 = page.getViewport({ scale: 1 });
        const scale = Math.min(width / v0.width, height / v0.height);
        const viewport = page.getViewport({ scale: Math.max(scale, 0.2) });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: false });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;
        const url = canvas.toDataURL("image/png");
        pdfThumbCache.set(src, url);
        setDataUrl(url);
        onDone && onDone();
      } catch (e) {
        if (!cancelled) {
          setFailed(true);
          onError && onError(e);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [src, width, height, dataUrl, onStart, onDone, onError]);

  if (failed) return null;
  if (!dataUrl) {
    return (
      <div style={{ width, height, display: "grid", placeItems: "center", fontSize: 11, color: "#888" }}>
        {skeleton}
      </div>
    );
  }
  return (
    <img
      src={dataUrl}
      alt="PDF preview"
      style={{ width, height, objectFit: "contain" }}
      loading="eager"
      decoding="async"
    />
  );
};

// Humanize the selected "source" value using available lookups
const humanizeSource = (value, { sourceOptions, imprintMethods }) => {
  if (!value) return "No decoration required";

  // In-house & custom quick labels
  if (value === "elite") return "Elite Decorations (In-House)";
  if (value === "supplier-custom") return "Custom Request (Supplier)";
  if (value === "supplier|$UNI") return "Unimprinted (Supplier)";

  // 3rd-party: thirdparty|<decoratorId>|<codeOr$UNI>
  if (value.startsWith("thirdparty|")) {
    const [, idStr, codeOrUni] = value.split("|");
    const d = EXTERNAL_DECORATORS.find(x => String(x.id) === String(idStr));
    const base = d ? d.label : "3rd-Party Decorator";
    return codeOrUni && codeOrUni !== "$UNI" ? `${base} (${codeOrUni})` : base;
  }

  // Supplier method: supplier|$<CODE>
  if (value.startsWith("supplier|$")) {
    const code = value.split("|")[1]; // e.g. "$0TW"
    // Try to find a pretty name from the options first
    const opt = sourceOptions?.find(o => o.value === value);
    if (opt?.label) return opt.label;

    // Fallback: if you have imprintMethods injected here, map by Code -> Name
    const method = imprintMethods?.find(m => m.Code === code);
    if (method?.Name) return `${method.Name} (Supplier)`;

    // Absolute fallback: show the code
    return `Supplier Method ${code}`;
  }

  // Generic fallback: try option label or echo the value
  const opt = sourceOptions?.find(o => o.value === value);
  return opt?.label || value;
};

const getEffectiveDecorationCost = (row = {}) =>
  getEffectiveDecorationCostFromRow(row, toNum);

const clearDecorationConversionFields = (row = {}) => ({
  ...row,
  ConvertedNetCost: null,
  convertedNetCost: null,
  ExchangeRate: null,
  exchangeRate: null,
  convertedCurrency: null,
});

const deriveRetailFromRow = (row = {}) =>
  deriveDecorationRetailFromRow({
    row,
    toNum,
    isLocked: isLockedBool(row?.isLocked),
  });

const deriveTotalFromRow = (row = {}) => {
  if (isLockedBool(row?.isLocked)) {
    const storedTotal = Number(row?.total);
    if (Number.isFinite(storedTotal)) return storedTotal;
  }
  const qty = isFixedChargeRow(row)
    ? 1
    : Math.max(0, parseInt(row.quantity, 10) || 0);
  return deriveRetailFromRow(row) * qty;
};




const Decoration = ({ setRows, clientFiles, handleDone, order, rows, orderItem, currentCartProduct = null, cartProducts = [], onCopyDecorationToProduct = null, customerId, productJson, decorationSource, imprintMethods, imprintLocations, imprintSizes, imprintColors, asiLiveRawData, onLocationChange, locationValue, sourceOptions, mapDecorationsFromApi, decorationEntirety, pricingRows = [], defaultMargin = 40, showNetCostLocks = false, isPromoStore = false, supportsPromoStoreArtworkChoices = false }) => {
    const decorationRef = useRef(null);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [activeDecorationIndex, setActiveDecorationIndex] = useState(null);
    const [showInternationalCostDropdown, setInternationalShowCostDropdown] = useState(false);
    const [activeRowIndex, setActiveRowIndex] = useState(null);
    const [activeArtworkChoiceIndex, setActiveArtworkChoiceIndex] = useState(null);
    const [decorationPopupOpen, setDecorationPopupOpen] = useState(false);
    const [manualChargeNameOverrides, setManualChargeNameOverrides] = useState(() => new Set());
    const [selectedRowInfo, setSelectedRowInfo] = useState({ decorationIndex: null, rowIndex: null });
    const [supplierLocations, setSupplierLocations] = useState([]);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copyActionMenu, setCopyActionMenu] = useState({ anchorEl: null, decorationIndex: null });
    const [copyToProductOpen, setCopyToProductOpen] = useState(false);
    const [copyToProductDecorationIndex, setCopyToProductDecorationIndex] = useState(null);
    const [copyTargetProductKey, setCopyTargetProductKey] = useState("");
    const [copyTargetMode, setCopyTargetMode] = useState("add");
    const [copyReplaceDecorationIndex, setCopyReplaceDecorationIndex] = useState("");
    const [copyingToProduct, setCopyingToProduct] = useState(false);

    const defaultDecorationQuantity = useMemo(
      () => calculateDefaultDecorationQuantity(pricingRows),
      [pricingRows]
    );
    const previousPricingQuantityTotalRef = useRef(defaultDecorationQuantity);
    const [copyFromItemId, setCopyFromItemId] = useState(null);
    const [imgBroken, setImgBroken] = useState({}); // key: `dec-${idx}` => true

    useEffect(() => {
      const previousPricingTotal = previousPricingQuantityTotalRef.current;
      const nextPricingTotal = defaultDecorationQuantity;

      if (previousPricingTotal !== nextPricingTotal) {
        setRows((prevRows) => {
          const { decorations, changed } = syncInitialDecorationRunChargeQuantities({
            decorations: prevRows,
            previousPricingTotal,
            nextPricingTotal,
          });
          return changed ? decorations : prevRows;
        });
      }

      previousPricingQuantityTotalRef.current = nextPricingTotal;
    }, [defaultDecorationQuantity, setRows]);

    const syncBinding = (decorationIndex, rowIndex) => {
      setRows(prev => {
        if (!Array.isArray(prev)) {
          console.error("[syncBinding] prev decorations is not array:", prev);
          return prev; // don't mutate to unknown shape
        }

           console.groupCollapsed(
               `%c[syncBinding] -> dec:${decorationIndex} row:${rowIndex}`,
               "color:#329dae;font-weight:700"
             );
             console.log("args", { decorationIndex, rowIndex });
    
        const next = prev.slice();
        const dec  = next[decorationIndex];
    
        if (!dec) {
          console.warn("[syncBinding] no decoration at index", decorationIndex);
          return next;
        }
        if (!Array.isArray(dec.rows)) {
          console.error("[syncBinding] dec.rows is not array:", dec.rows);
          return next;
        }
    
        const row = dec.rows[rowIndex];

               console.log("row snapshot", {
                  exists: !!row,
                   rowIndex,
                   chargeId: row?.chargeId ?? null,
                   chargeName: row?.chargeName ?? "",
                   decoratorId: row?.decoratorId ?? null,
                   supplierId: row?.supplierId ?? null,
                   addressId: row?.addressId ?? null,
                   quantity: row?.quantity ?? null
                 });

        if (!row) {
          console.warn("[syncBinding] no row at index", { decorationIndex, rowIndex });
          return next;
        }
    
        // ensure DecoratorDetails exists
        const dd = { ...(dec.DecoratorDetails || {}) };
    
        // Build/replace the binding for this row
        const bind = {
          rowIndex,
          chargeId:    row.chargeId ?? null,
          chargeName:  row.chargeName ?? "",
          decoratorId: row.decoratorId ?? dd.decoratorId ?? null,
          supplierId:  row.supplierId ?? null,
          addressId:   row.addressId ?? null,
        };
    
        const prevBindings = Array.isArray(dd.rowBindings) ? dd.rowBindings : [];
        const withoutThis  = prevBindings.filter(b => Number(b.rowIndex) !== Number(rowIndex));
    
        next[decorationIndex] = {
          ...dec,
          DecoratorDetails: {
            ...dd,
            rowBindings: [...withoutThis, bind],
          },
        };
    
        // sanity log
        console.log("[syncBinding] wrote binding:", { decorationIndex, rowIndex, bind });
        return next;
      });
    };

    // Always give me an array (never objects / null / strings)
    const asArray = (v) => (Array.isArray(v) ? v : []);

    // Minimal normalizer for each decoration entry (keeps UI shape)
    const normalizeDecorationEntry = (d) => normalizeDecorationShape(d || {});
    const normalizeDecorationsArray = (v) => asArray(v).map(normalizeDecorationEntry);

    // Reject non-array writes and log who did it
    const setRowsSafe = (nextOrFn) =>
      setRows((prev) => {
        const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
        if (!Array.isArray(next)) {
          console.error("[setRowsSafe] rejecting non-array write", next);
          console.log("[setRowsSafe] previous value:", prev);
          console.trace();
          try { window.__badRowsWrite = { when: new Date().toISOString(), next, prev }; } catch {}
          return prev;
        }
        // normalize every time
        return normalizeDecorationsArray(next);
      });    

    // clears a stale "broken image" flag for a given decoration index
    const clearBrokenFor = (idx) =>
    setImgBroken((m) => {
      const key = `dec-${idx}`;
      if (!m || !m[key]) return m;
      const copy = { ...m };
      delete copy[key];
      return copy;
    });


    /* Clear/delete row of decoration */

    const [confirmDeco, setConfirmDeco] = useState({
        open: false,
        action: null,             // 'delete' | 'clear'
        decorationIndex: null,
    });

// Remove a decoration instance entirely
const deleteDecoration = (decorationIndex) => {
  setRows(prev => {
    const arr = Array.isArray(prev) ? prev : [];
    if (decorationIndex < 0 || decorationIndex >= arr.length) return arr;
    // remove the decoration at index
    const next = arr.filter((_, i) => i !== decorationIndex);
    return next;
  });

  // tidy up any UI state that might be pointing at the removed decoration
  if (activeDecorationIndex === decorationIndex) {
    setActiveDecorationIndex(null);
    setUploadDialogOpen(false);
  }
  if (selectedRowInfo?.decorationIndex === decorationIndex) {
    setSelectedRowInfo({ decorationIndex: null, rowIndex: null });
    setDecorationPopupOpen(false);
  }
};


// Reset a single decoration to a blank state (keeps the slot, wipes fields)
const clearDecoration = (decorationIndex) => {
    const newDecorations = [...asArray(rows)];
    if (!newDecorations[decorationIndex]) return;
  
    const defaultLocation = orderItem?.Imprinting?.Locations?.Values?.[0]?.Name || '';
    newDecorations[decorationIndex] = {
      id: "",
      Location: defaultLocation || "",
      image: null,
      DecoratorDetails: {
        ...(newDecorations[decorationIndex].DecoratorDetails || {}),
        // keep the chosen source so the user doesn't have to re-select
        source: newDecorations[decorationIndex].DecoratorDetails?.source || "",
        promoStoreSelectionMode:
          normalizePromoStoreSelectionMode(newDecorations[decorationIndex].DecoratorDetails?.promoStoreSelectionMode) ||
          (isPromoStore ? "optional" : ""),
      },
      artwork: {
        imprintType: '',
        charge: 0,
        proofRequired: '',
        proofRequiredAction: null,
        logoName: '',
        logoSize: '',
        logoColor: '',
        repeatLogo: '',
        supplierNotes: '',
        selectedImage: '',
        customerFileId: null,
      },
      rows: [{
        id: '',
        chargeName: '',
        type: "breakdown",
        quantity: defaultDecorationQuantity,
        netCost: 0,
        margin: defaultMargin,
        retail: 0,
        clientPrice: 0,
        total: 0,
        isRunCharge: false,
        pricingOption: 'Display to Client',
        decoratorId: null,
        chargeId: null,
        supplierId: null,
        addressId: null,
        isLocked: false,
        artwork: {
          imprintType: '',
          proofRequired: '',
          charge: 0,
          logoName: '',
          logoSize: '',
          logoColor: '',
          repeatLogo: '',
          supplierNotes: '',
          selectedImage: '',
          customerFileId: null,
        }
      }]
    };
  
    setRows(newDecorations);
  };

  ;

  const rebaseBindingsAfterRemove = (decorationIndex, removedIndex) => {
    setRows(prev => {
      const next = Array.isArray(prev) ? prev.slice() : prev;
      const dec  = next?.[decorationIndex];
      if (!dec) return next;
      const dd   = { ...(dec.DecoratorDetails || {}) };
      const binds = Array.isArray(dd.rowBindings) ? dd.rowBindings : [];
  
      dd.rowBindings = binds
        .filter(b => Number(b.rowIndex) !== Number(removedIndex))     // drop the removed one
        .map(b => {
          const idx = Number(b.rowIndex);
          return idx > removedIndex ? { ...b, rowIndex: idx - 1 } : b; // shift the rest down
        });
  
      next[decorationIndex] = { ...dec, DecoratorDetails: dd };
      return next;
    });
  };  

  
  // Open confirm dialog
  const requestConfirmDecoration = (action, decorationIndex) => {
    setConfirmDeco({ open: true, action, decorationIndex });
  };
  
  // Handle confirm dialog actions
  const handleConfirmDecoration = () => {
    const { action, decorationIndex } = confirmDeco;
    if (decorationIndex == null) return;
  


    if (action === 'clear') clearDecoration(decorationIndex);
    if (action === 'delete') deleteDecoration(decorationIndex);
  
    setConfirmDeco({ open: false, action: null, decorationIndex: null });
  };
  
  const handleCancelConfirmDecoration = () =>
    setConfirmDeco({ open: false, action: null, decorationIndex: null });
  

    /* Performance */

    const items = useMemo(() => {
        if (Array.isArray(cartProducts) && cartProducts.length) return cartProducts;
        if (Array.isArray(order?.Items) && order.Items.length) return order.Items;
        if (Array.isArray(order?.items) && order.items.length) return order.items;
        if (Array.isArray(order?.Products)) return order.Products;
        return [];
      }, [cartProducts, order]);

    const currentCopyProduct = currentCartProduct || orderItem || {};
    const copyTargetItems = useMemo(
      () => (items || []).filter((item) => !isSameCartProduct(item, currentCopyProduct)),
      [currentCopyProduct, items]
    );

    const safeMap = (raw, defaultLocation) => {
        try {
            const arr = Array.isArray(raw) ? raw : (typeof raw === "string" ? JSON.parse(raw) : []);
            return (typeof mapDecorationsFromApi === "function"
            ? mapDecorationsFromApi(arr, defaultLocation, sourceOptions)
            : []) || [];
        } catch {
            return [];
        }
    };

      // top-level hooks in Decoration.jsx
        const pendingOpenRef = useRef(null);
        const hydratedOnceRef = useRef(false);

        useEffect(() => {
        if (pendingOpenRef.current) {
            const { decorationIndex, rowIndex } = pendingOpenRef.current;
            setSelectedRowInfo({ decorationIndex, rowIndex });
            setDecorationPopupOpen(true);
            pendingOpenRef.current = null;
        }
        }, [rows]);

        // One-time, fast client-side hydration of artwork onto decorations for instant preview
        useEffect(() => {
          // only run once per open or until we see art attached
          if (hydratedOnceRef.current) return;
          if (!Array.isArray(rows) || rows.length === 0) return;

          // flat artworks coming from the item payload (cached or live)
          const flatArts = orderItem?.artworks ?? orderItem?.Artworks ?? [];

          // do we actually need to hydrate?
          const needsHydrate = rows.some(d => {
            const locHasImg = !!(d?.artwork?.selectedImage || d?.ArtWork?.Image);
            const hasFlatForThisDeco = flatArts.some(a => String(a.DecorationId) === String(d?.id));
            return !locHasImg && hasFlatForThisDeco;
          });

          if (!needsHydrate) {
            hydratedOnceRef.current = true;
            return;
          }

          // stitch flat artworks -> decorations, then normalize UI shape
          const merged = attachArtworksToDecorations(rows, flatArts).map(normalizeDecorationShape);
          setRows(merged);

          // warm the browser image cache so thumbnails pop instantly
          merged.forEach(d => {
            const url = d?.artwork?.selectedImage;
            if (!url) return;
            const img = new Image();
            img.decoding = 'async';
            img.loading  = 'eager';
            img.src = url;
          });

          hydratedOnceRef.current = true;
        }, [rows, orderItem]);

      
        useEffect(() => {
          // if a decoration has a new image now, clear any stale broken flags for it
            asArray(rows).forEach((d, i) => {
            const key = `dec-${i}`;
            if (imgBroken[key] && d?.artwork?.selectedImage) {
              clearBrokenFor(i);
            }
          });
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [rows]);        
      
      // put with your helpers
const probeArt = (label, arr) => {
    console.groupCollapsed(`🎯 ${label}`);
    (arr || []).slice(0, 5).forEach((d, i) => {
      console.log(`#${i}`, {
        loc: d?.Location,
        loc_artwork_image: d?.artwork?.selectedImage,
        loc_ArtWork_Image: d?.ArtWork?.Image,
        row_has_any: Array.isArray(d?.rows) && d.rows.some(r => r?.artwork?.selectedImage || r?.ArtWork?.Image),
        row_samples: (d?.rows || []).slice(0,3).map(r => ({
          chargeName: r?.chargeName || r?.Name,
          row_artwork_image: r?.artwork?.selectedImage,
          row_ArtWork_Image: r?.ArtWork?.Image
        })),
        decorator_source: d?.DecoratorDetails?.source
      });
    });
    console.groupEnd();
  };
  
      

// Decoration.jsx (helpers below other fns)
const deepClone = (obj) => {
    if (obj == null) return obj; // undefined or null
    if (typeof window !== "undefined" && typeof window.structuredClone === "function") {
      try { return window.structuredClone(obj); } catch {}
    }
    return JSON.parse(JSON.stringify(obj));
  };

  function normalizeArtworkFields(a = {}, fallbackImg = null) {
    const num = (v, d = 0) => (v === "" || v == null ? d : Number(v));
    const pick = (lo, up, d = "") =>
      lo != null && lo !== "" ? lo : (up != null ? up : d);
  
    const proofRequiredAction = a.proofRequiredAction ?? a.ProofRequiredAction ?? null;
  
    return {
      id: a.id ?? a.ID ?? null,
      selectedImage: a.selectedImage ?? a.Image ?? fallbackImg ?? null,
      previewUrl: getArtworkPreviewUrl(a) || null,
      imprintType:          pick(a.imprintType,          a.ImprintType,          ""),
      customerFileId:       pick(a.customerFileId,       a.CustomerFileId,       null),
      charge:               num(a.charge ?? a.Charge, 0),           // ← typed
      proofRequired:        pick(a.proofRequired,        a.ProofRequired,        ""),
      proofRequiredAction:  proofRequiredAction != null ? Number(proofRequiredAction) : null,
      logoName:             pick(a.logoName,             a.LogoName,             ""),
      logoSize:             pick(a.logoSize,             a.LogoSize,             ""),
      logoColor:            pick(a.logoColor,            a.LogoColor,            ""),
      repeatLogo:           pick(a.repeatLogo,           a.RepeatLogo,           ""),
      supplierNotes:        pick(a.supplierNotes,        a.SupplierNotes,        ""),
    };
  }
  

// Join detached artworks back onto each decoration by DecorationId
function attachArtworksToDecorations(decorations, artworks) {
    const decs = Array.isArray(decorations) ? decorations : [];
    const arts = Array.isArray(artworks) ? artworks : [];
  
    return decs.map(d => {
      const matched = arts.filter(x => String(x.DecorationId ?? x.PromoStoreItemDecorationId) === String(d.id));
      if (!matched.length) return d;
  
      const ArtWorks = matched.map(a => ({
        id: a.id ?? null,
        ImprintType:          a.ImprintType ?? "",
        Image:                a.Image ?? null,
        PreviewUrl:           a.PreviewUrl ?? null,
        CustomerFileId:       a.CustomerFileId ?? null,
        ProofRequired:        a.ProofRequired ?? "",
        ProofRequiredAction:  a.ProofRequiredAction ?? null,
        LogoName:             a.LogoName ?? "",
        LogoSize:             a.LogoSize ?? "",
        LogoColor:            a.LogoColor ?? "",
        RepeatLogo:           a.RepeatLogo ?? "",
        SupplierNotes:        a.SupplierNotes ?? "",
        Charge:               a.Charge ?? 0,
      }));
      const ArtWork = ArtWorks[0] || null;
  
      const merged = { ...d, ArtWorks, ArtWork };
  
      // Also seed UI-style artwork for preview; normalizer will finish the job
      if (!merged.artwork) merged.artwork = {};
      if (!merged.artwork.selectedImage && ArtWork?.Image) {
        merged.artwork.selectedImage = ArtWork.Image;
      }
      if (!merged.artwork.previewUrl && getArtworkPreviewUrl(ArtWork)) {
        merged.artwork.previewUrl = getArtworkPreviewUrl(ArtWork);
      }
  
      return merged;
    });
  }
  
  
// Normalize API shapes to UI shapes (ArtWork -> artwork)
const normalizeDecorationShape = (dec) => {
    if (!dec) return dec;
    const out = { ...dec };
  
    // Map API Charge[] → UI rows if needed
    if (!Array.isArray(out.rows) && Array.isArray(out.Charge)) {
      out.rows = out.Charge.map(c => ({
        id:            c.id ?? "",
        chargeName:    c.Name ?? "",
        quantity:      c.Quantity ?? 0,
        netCost:       c.NetCost ?? 0,
        margin:        c.Margin ?? 0,
        retail:        c.Retail ?? 0,
        clientPrice:   c.ClientPrice ?? 0,
        total:         c.Total ?? 0,
        type:         c.Type ?? c.type ?? "",
        subType:      c.SubType ?? c.subType ?? "",
        // Charge[] uses SubType, while newly-added UI rows use isRunCharge.
        // Restore the UI flag so saved run charges are never displayed as fixed.
        isRunCharge:  ["run", "run charge"].includes(
          String(c.SubType ?? c.subType ?? c.priceType ?? "").trim().toLowerCase()
        ),
        priceType:     c.Type === "BreakDown" ? "Breakdown"
                      : c.SubType === "Run Charge" ? "Run Charge"
                      : c.SubType === "Fixed Charge" ? "Fixed Charge"
                      : "",
        pricingOption: c.DisplayStatus ?? "Display to Client",
        supplierId:    c.supplierId ?? null,
        addressId:     c.addressId ?? null,
        decoratorId:   c.decoratorId ?? null,
        chargeId:      c.chargeId ?? null,
        // Preserve explicit routing through API Charge -> UI rows -> copied Charge.
        // A row binding may carry the value on older saved decorations.
        productionDepartmentId: c.productionDepartmentId ?? c.ProductionDepartmentId ??
          (out.DecoratorDetails?.rowBindings ?? out.DecoratorDetails?.RowBindings ?? [])
            .find(binding => Number(binding?.rowIndex) === out.Charge.indexOf(c))?.productionDepartmentId ?? null,
        CurrencyCode:  c.CurrencyCode ?? c.currencyCode ?? null,
        currencyCode:  c.currencyCode ?? c.CurrencyCode ?? null,
        PricingCurrencyCode: c.PricingCurrencyCode ?? c.pricingCurrencyCode ?? null,
        pricingCurrencyCode: c.pricingCurrencyCode ?? c.PricingCurrencyCode ?? null,
        ConvertedNetCost: c.ConvertedNetCost ?? c.convertedNetCost ?? null,
        convertedNetCost: c.convertedNetCost ?? c.ConvertedNetCost ?? null,
        ExchangeRate: c.ExchangeRate ?? c.exchangeRate ?? null,
        exchangeRate: c.exchangeRate ?? c.ExchangeRate ?? null,
        convertedCurrency: c.convertedCurrency ?? null,
        isLocked:      c.IsLocked ?? c.isLocked ?? false,
  
        // carry through any row-level artwork blobs; normalized below
        artwork:       c.artwork || c.ArtWork || {},
        ArtWork:       c.ArtWork || undefined,
      }));
    }
  
    // ---- Location-level artwork ----
    // Prefer merging both shapes so nothing is lost.
    const incomingArtWorks = Array.isArray(out.ArtWorks)
      ? out.ArtWorks
      : Array.isArray(out.artWorks)
        ? out.artWorks
        : [];
    const normalizedArtWorks = (incomingArtWorks.length ? incomingArtWorks : [out.ArtWork].filter(Boolean))
      .map((art) => normalizeArtworkFields(art));
    const firstArtWork = normalizedArtWorks[0] || out.ArtWork || {};
    // Blank UI placeholders must not erase saved logo colours or other artwork metadata.
    const populatedUiArtwork = Object.fromEntries(Object.entries(out.artwork || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined));
    const locArtSrc = { ...(firstArtWork || {}), ...populatedUiArtwork };
    const locFallbackImg =
      out.image ??
      out.Image ??
      out.artwork?.Image ??
      out.ArtWork?.Image ??
      null;
  
    out.artwork = normalizeArtworkFields(locArtSrc, locFallbackImg);
    out.ArtWorks = normalizedArtWorks.length ? normalizedArtWorks : [out.artwork];
  
    // ---- Row-level artwork ----
    if (Array.isArray(out.rows)) {
      out.rows = out.rows.map(r => {
        const rr = { ...r };
        const rowArtSrc = { ...(rr.ArtWork || {}), ...(rr.artwork || {}) };
        rr.artwork = normalizeArtworkFields(rowArtSrc);
        rr.isLocked = rr?.isLocked ?? rr?.IsLocked ?? false;
        // Existing UI rows can omit routing while their saved row binding retains it.
        const binding = (out.DecoratorDetails?.rowBindings ?? out.DecoratorDetails?.RowBindings ?? [])
          .find(binding => Number(binding?.rowIndex) === out.rows.indexOf(r));
        rr.productionDepartmentId = rr.productionDepartmentId ?? rr.ProductionDepartmentId ??
          binding?.productionDepartmentId ?? binding?.ProductionDepartmentId ?? null;
        delete rr.ArtWork; // keep UI shape clean
        return rr;
      });
    }
  
    // If location still lacks an image, bubble from the first row that has one
    if (!out.artwork.selectedImage && Array.isArray(out.rows)) {
      const rowWithImg = out.rows.find(r => r?.artwork?.selectedImage);
      if (rowWithImg?.artwork?.selectedImage) {
        out.artwork.selectedImage = rowWithImg.artwork.selectedImage;
      }
    }
  
    // Preview helper for the rest of the UI
    out.image = out.artwork.selectedImage ?? null;
  
    // Clean up legacy fields we no longer need in the UI shape
    delete out.ArtWork;
  
    return out;
  };
  
  
  
  
// 2) keepBindings & keepArtwork default to true so copy preserves IDs + artwork
const scrubDecorationForCopy = (incoming, { keepBindings = true, keepArtwork = true } = {}) => {
    const src = normalizeDecorationShape(incoming); // <— ensure shape first
    const d = deepClone(src);
  
    d.id = "";
  
    if (!d.DecoratorDetails) d.DecoratorDetails = {};
    d.DecoratorDetails.source = d.DecoratorDetails.source || "";
    if (!keepBindings) {
      d.DecoratorDetails.decoratorId = null;
      d.DecoratorDetails.chargeId    = null;
    }
  
      d.rows = (Array.isArray(d.rows) ? d.rows : []).map(r => ({
      ...r,
      id: "",
      decoratorId: keepBindings ? (r.decoratorId ?? null) : null,
      chargeId:    keepBindings ? (r.chargeId    ?? null) : null,
      supplierId:  r.supplierId ?? null,
      addressId:   r.addressId  ?? null,
      isLocked:    r?.isLocked ?? false,
      ...(keepArtwork
        ? {
            artwork: (() => {
              const copiedArtwork = deepClone(r.artwork || { selectedImage: null });
              delete copiedArtwork.id;
              return copiedArtwork;
            })(),
          }
        : { artwork: undefined }
      ),
    }));
  
    if (keepArtwork) {
      d.artwork = deepClone(d.artwork || { selectedImage: null });
      delete d.artwork.id;
      d.ArtWorks = (Array.isArray(d.ArtWorks) ? d.ArtWorks : []).map((artwork) => {
        const copiedArtwork = deepClone(artwork || {});
        delete copiedArtwork.id;
        return copiedArtwork;
      });
      if (Array.isArray(d.artWorks)) {
        d.artWorks = d.artWorks.map((artwork) => {
          const copiedArtwork = deepClone(artwork || {});
          delete copiedArtwork.id;
          return copiedArtwork;
        });
      }
  
      // If location still has no image, try promoting from first row
      if (!d.artwork.selectedImage) {
        const rowWithImg = d.rows.find(r => r?.artwork?.selectedImage);
        if (rowWithImg?.artwork?.selectedImage) {
          d.artwork.selectedImage = rowWithImg.artwork.selectedImage;
        }
      }
  
      d.image = d.artwork.selectedImage ?? null;
    } else {
      if (d.artwork) d.artwork.selectedImage = "";
      d.image = null;
    }
  
    return d;
  };
  
  
  

// Paste inside Decoration.jsx (uses: decorationEntirety, safeMap, scrubDecorationForCopy,
// rows, setRows, setCopyDialogOpen, setCopyFromItemId, pendingOpenRef)
// Decoration.jsx
// --- Put near top-level helpers ---
const DEBUG_COPY = true;

function safeJson(s) {
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; }
  catch { return []; }
}

function hasArtwork(dec) {
  // location-level
  if (dec?.artwork?.selectedImage || dec?.ArtWork?.Image) return true;
  // row-level
  if (Array.isArray(dec?.rows)) {
    return dec.rows.some(r =>
      r?.artwork?.selectedImage || r?.ArtWork?.Image
    );
  }
  return false;
}

function brief(dec) {
  if (!dec) return null;
  return {
    id: dec.id || "",
    loc: dec.Location || "",
    hasLocArt: !!(dec?.artwork?.selectedImage || dec?.ArtWork?.Image),
    rowArtCount: Array.isArray(dec.rows)
      ? dec.rows.filter(r => r?.artwork?.selectedImage || r?.ArtWork?.Image).length
      : 0,
    src: dec?.DecoratorDetails?.source || "",
  };
}

// --- Drop-in replacement ---
const handleCopyFromItem = async (sourceItem, { openEditor = false } = {}) => {
    if (!sourceItem) return;
  
    const defaultLocation =
      sourceItem?.Imprinting?.Locations?.Values?.[0]?.Name
      || orderItem?.Imprinting?.Locations?.Values?.[0]?.Name
      || "";
  
    const group = (label) => DEBUG_COPY && console.groupCollapsed(label);
    const end   = () => DEBUG_COPY && console.groupEnd();
    group("🧪 COPY DEBUG: handleCopyFromItem()");
  
    // A) raw decorations + parse if string
    const raw = sourceItem.decorations ?? sourceItem.Decorations ?? [];
    const rawArr0 = Array.isArray(raw) ? raw : (typeof raw === "string" ? safeJson(raw) : []);
  
    // 🚩 merge detached artworks onto decorations (critical piece)
    const artworksArr = sourceItem.artworks ?? sourceItem.Artworks ?? [];
    const rawArr = attachArtworksToDecorations(rawArr0, artworksArr);
  
    DEBUG_COPY && console.log("RAW length (after merge):", rawArr.length, rawArr.slice(0, 3).map(brief));
  
    // B) background mapped (optionally also merge, in case mapper didn't)
    const entry  = decorationEntirety?.byItemId?.[sourceItem.id];
    const mapped0 = Array.isArray(entry?.mapped) ? entry.mapped : [];
    const mapped  = attachArtworksToDecorations(mapped0, artworksArr);
  
    // C) on-the-spot mapped (if needed). You can attach after mapping too:
    let mappedOnTheSpot = [];
    if (!mapped || mapped.length === 0) {
      mappedOnTheSpot = safeMap(rawArr, defaultLocation);
      mappedOnTheSpot = attachArtworksToDecorations(mappedOnTheSpot, artworksArr);
    }
  
    // D) normalize the RAW (now it contains ArtWork/Image)
    const normalizedFromRaw = rawArr.map(d => normalizeDecorationShape(d));
  
    // ...rest of your selection logic unchanged...
    // prefer mapped with art → else normalizedFromRaw → else fallback
    const mappedHasAnyArtwork        = mapped.some(hasArtwork);
    const normalizedRawHasArtwork    = normalizedFromRaw.some(hasArtwork);
    const fallbackHasArtwork         = mappedOnTheSpot.some(hasArtwork);
  
    let sourceDecorations;
    if (mappedHasAnyArtwork) {
      sourceDecorations = mapped;
    } else if (normalizedRawHasArtwork) {
      sourceDecorations = normalizedFromRaw;
    } else if (fallbackHasArtwork) {
      sourceDecorations = mappedOnTheSpot;
    } else {
      sourceDecorations = mapped.length
        ? mapped
        : mappedOnTheSpot.length ? mappedOnTheSpot : normalizedFromRaw;
    }
  
    const sourcePricingRaw =
      sourceItem?.pricingRows ?? sourceItem?.pricings ?? sourceItem?.Pricings ??
      sourceItem?.Pricing ?? sourceItem?.pricing ?? [];
    const sourcePricingRows = asArray(sourcePricingRaw);
    const sourceBreakdowns = sourcePricingRows.filter((row) =>
      String(row?.priceType ?? row?.Type ?? row?.type ?? "").toLowerCase() === "breakdown"
    );
    const pricedQuantity = sourceBreakdowns.length
      ? sourceBreakdowns.reduce((sum, row) =>
          sum + (Number(row?.quantity ?? row?.Quantity) || 0), 0)
      : null;
    // Some older cart entries expose only decoration charges, not pricing rows.
    // In that case, infer the original run quantity without guessing from fixed charges.
    const sourceRunQuantity = (Array.isArray(sourceDecorations) ? sourceDecorations : [])
      .flatMap((decoration) => scrubDecorationForCopy(decoration).rows || [])
      .find((row) => String(row?.priceType ?? row?.type ?? row?.SubType ?? "").toLowerCase() === "run charge")
      ?.quantity;
    const sourceQuantity = pricedQuantity > 0
      ? pricedQuantity
      : (Number(sourceRunQuantity) > 0 ? Number(sourceRunQuantity) : null);
    const targetQuantity = defaultDecorationQuantity;
    const quantityDiffers = sourceQuantity != null && sourceQuantity > 0 &&
      sourceQuantity !== targetQuantity;
    if (quantityDiffers && !window.confirm(
      `The source product has ${sourceQuantity} units and this product has ${targetQuantity}. ` +
      `Copy the decorations and adjust their run-charge quantities and totals to ${targetQuantity}?\n\n` +
      "Cancel leaves the destination unchanged."
    )) return;

    let verifiedSources;
    try {
      verifiedSources = await Promise.all(
        (Array.isArray(sourceDecorations) ? sourceDecorations : []).map(async (d) =>
          resolveMatrixChargeTypesForCopy(
            scrubDecorationForCopy(d, { keepBindings: true, keepArtwork: true }),
            getSingleDecoratorDetails
          )
        )
      );
    } catch (error) {
      window.alert(error?.message || "Unable to verify decoration charge types for copy.");
      return;
    }
    const cleaned = verifiedSources
      .map((decoration) => {
        // A copied run charge belongs to the destination product, even when the source product total matches.
        // Leave fixed/setup charges unchanged.

        return {
          ...decoration,
          rows: (decoration.rows || []).map((row, index) => {
            const classification = String(
              row?.subType || row?.SubType || row?.priceType || row?.chargeType || row?.ChargeType || ""
            ).trim().toLowerCase();
            const isRunCharge = classification === "run charge" || classification === "run";
            if (!isRunCharge) return row;
            return {
              ...row,
              ...decorationPricing.recalculateDecorationRowForQuantity(row, targetQuantity),
            };
          }),
        };
      });
  
    // (optional) debug to verify artwork presence
    probeArt("CLEANED (final to setRows)", cleaned);

    // G) Append into current rows
    const base = asArray(rows);
    const startIdx = base.length;
    const next = [...base, ...cleaned];

    // H) Optionally open first new row editor
    if (openEditor) {
    const firstIdx = next.findIndex(
        (d, i) => i >= startIdx && Array.isArray(d.rows) && d.rows.length > 0
    );
    if (firstIdx !== -1) {
        pendingOpenRef.current = { decorationIndex: firstIdx, rowIndex: 0 };
    }
    }

    // I) Commit to state
    setRows(next);

    // J) Preload images for instant preview
    cleaned.forEach(d => {
    const url = d?.artwork?.selectedImage;
    if (!url) return;
    const img = new Image();
    img.decoding = "async";
    img.loading  = "eager";
    img.src = url;
    });

    // K) Close dialog / reset selection
    setCopyDialogOpen(false);
    setCopyFromItemId(null);

    end(); // close the debug group
    end();
  };
  

  
    // Decoration.jsx
    const getOptionsFromSource = (source) => {
      const sizesAll  = asiLiveRawData?.Imprinting?.Sizes?.Values?.map(s => ({ label: s.Name, value: s.Name })) || [];
      const colorsAll = asiLiveRawData?.Imprinting?.Colors?.Values?.map(c => ({ label: c.Name, value: c.Name })) || [];
    
      if (!source) return { imprintLocations: [], imprintSizes: sizesAll, imprintColors: colorsAll };
    
      // 3rd-party: thirdparty|<decoratorId>|<codeOr$UNI>
      if (source.startsWith("thirdparty|")) {
        const parts = source.split("|"); // ["thirdparty", "<id>", "<codeOr$UNI>"]
        const code = parts[2] || "$UNI";
        if (code === "$UNI") {
          return { imprintLocations: [], imprintSizes: sizesAll, imprintColors: colorsAll };
        }
        // If you later store per-decorator methods, look them up by (decoratorId, code) here
        const locs = []; // for now, none
        return { imprintLocations: locs, imprintSizes: sizesAll, imprintColors: colorsAll };
      }
    
      // Supplier general
      if (source === "$UNI" || source === "supplier|$UNI") {
        return { imprintLocations: [], imprintSizes: sizesAll, imprintColors: colorsAll };
      }
    
      // Existing supplier/elite method logic (your current code)
      const [prefix, code] = source.includes("|") ? source.split("|") : [null, source];
      const method = imprintMethods.find(m =>
        String(m.Code ?? "").toLowerCase() === String(code ?? "").toLowerCase()
      );
      const locs = method?.Options?.find(o => o.Type === "Imprint Location")?.Values?.map(v => ({ label: v.Name, value: v.Name })) || [];
      return { imprintLocations: locs, imprintSizes: sizesAll, imprintColors: colorsAll };
    };
    
    
  
  const proofRequiredOptions = [
    { id: 0, name: 'None' },
    { id: 1, name: 'Designer required' },
    { id: 2, name: 'Supplier provided' }
  ];

  const handleRowChange = (decorationIndex, rowIndex, field, rawValue) => {
    setRows((prevRows) => {
      const next = [...prevRows];
      const dec = next[decorationIndex];
      if (!dec || !Array.isArray(dec.rows) || !dec.rows[rowIndex]) return prevRows;

      const line = { ...dec.rows[rowIndex] };
      const locked = showNetCostLocks && isLockedBool(line?.isLocked);
      const value = String(rawValue ?? "");

      if (locked) {
        if (field !== "netCost") return prevRows;
        if (!DECIMAL_RX.test(value)) return prevRows;

        line.netCost = value;
        if (hasConvertedCostBasis(line)) {
          Object.assign(line, clearDecorationConversionFields(line));
        }

        const nextRows = [...dec.rows];
        nextRows[rowIndex] = line;
        next[decorationIndex] = { ...dec, rows: nextRows };

        return next;
      }

      // 1) validate + store as STRING so user can type "." etc.
      if (["netCost", "margin", "retail", "clientPrice"].includes(field)) {
        if (!DECIMAL_RX.test(value)) return prevRows;
        line[field] = value;
        if (field === "netCost" && hasConvertedCostBasis(line)) {
          Object.assign(line, clearDecorationConversionFields(line));
        }
      } else if (field === "quantity") {
        // The existing Fixed Charge UI is read-only; keep that true in state too.
        if (isFixedChargeRow(line) || !INTEGER_RX.test(value)) return prevRows;
        line[field] = value;
      } else if (field === "subType" && ["Fixed Charge", "Run Charge"].includes(rawValue)) {
        Object.assign(line, applyMatrixChargeType(
          line, rawValue === "Fixed Charge" ? "Fixed" : "Run", defaultDecorationQuantity
        ));
      } else if (field === "pricingOption") {
        const isFixedCharge = isFixedChargeRow(line);
        const allowed = isFixedCharge
          ? options.map((opt) => opt.id)
          : optionsForNormalCost.map((opt) => opt.id);
        line[field] = normalizeDisplayStatus(rawValue, {
          allowed,
          fallback: DISPLAY_STATUS.DISPLAY_TO_CLIENT,
        });
      } else {
        line[field] = rawValue;
      }

      // 2) compute numbers from strings (don't write back formatted strings here)
      const netCost = parseFloat(line.netCost) || 0;
      const margin = parseFloat(line.margin) || 0;
      const isFixedCharge = isFixedChargeRow(line);
      const quantity = isFixedCharge ? 1 : (parseInt(line.quantity, 10) || 1);

      if (field === "netCost" || field === "margin" || field === "pricingOption") {
        const retail = resolveDecorationRetailFromInputs({
          netCostValue: netCost,
          marginValue: margin,
          pricingOptionValue: line.pricingOption,
          retailValue: line.retail,
          clientPriceValue: line.clientPrice,
          changedField: field,
        });
        line.retail = Number.isFinite(retail) ? retail.toFixed(2) : "";
      }

      const effectiveRetail = resolveDecorationRetailFromInputs({
        netCostValue: netCost,
        marginValue: margin,
        pricingOptionValue: line.pricingOption,
        retailValue: line.retail,
        clientPriceValue: line.clientPrice,
        changedField: field,
      });
      line.clientPrice = effectiveRetail.toFixed(2);
      line.total = (effectiveRetail * quantity).toFixed(2);

      if (field === "retail") {
        const retailEditedLine = applyRetailEditToDecorationRow({
          row: line,
          retailValue: effectiveRetail,
          effectiveCost: getEffectiveDecorationCost(line),
          quantity,
        });
        line.margin = retailEditedLine.margin.toFixed(2);
        line.Margin = retailEditedLine.Margin;
        line.clientPrice = retailEditedLine.clientPrice.toFixed(2);
        line.total = retailEditedLine.total.toFixed(2);
      }

      const nextRows = [...dec.rows];
      nextRows[rowIndex] = line;
      next[decorationIndex] = { ...dec, rows: nextRows };
      return next;
    });
  };
  
  const formatRow = (decorationIndex, rowIndex) => {
    setRows((prevRows) => {
      const next = [...prevRows];
      const dec  = next[decorationIndex];
      if (!dec || !Array.isArray(dec.rows) || !dec.rows[rowIndex]) return prevRows;

      const line = { ...dec.rows[rowIndex] };
      const locked = showNetCostLocks && isLockedBool(line?.isLocked);

      if (locked) {
        line.netCost = normalizeEditableDecimalForCommit(line.netCost);

        const nextRows = [...dec.rows];
        nextRows[rowIndex] = line;
        next[decorationIndex] = { ...dec, rows: nextRows };
        return next;
      }

      // Net Cost, Margin, and Retail are user-entered pricing inputs. Preserve
      // their entered precision on commit; the idle display formatter is solely
      // responsible for showing a minimum of two decimal places.
      line.netCost     = normalizeEditableDecimalForCommit(line.netCost);
      line.margin      = normalizeEditableDecimalForCommit(line.margin);
      line.retail      = normalizeEditableDecimalForCommit(line.retail);
      // Calculated money outputs retain their established two-decimal precision.
      line.clientPrice = as2(line.clientPrice);
      line.total       = as2(line.total);
      line.quantity    = asI(line.quantity);

      const nextRows = [...dec.rows];
      nextRows[rowIndex] = line;
      next[decorationIndex] = { ...dec, rows: nextRows };
      return next;
    });
  };

  const toggleDecorationLock = (decorationIndex, rowIndex) => {
    setRows((prevRows) => {
      const next = [...prevRows];
      const dec = next[decorationIndex];
      if (!dec || !Array.isArray(dec.rows) || !dec.rows[rowIndex]) return prevRows;

      const nextRows = [...dec.rows];
      const row = nextRows[rowIndex];
      nextRows[rowIndex] = {
        ...row,
        isLocked: !isLockedBool(row?.isLocked),
      };

      next[decorationIndex] = {
        ...dec,
        rows: nextRows,
      };

      return next;
    });
  };
  

    const addRow = (decorationIndex, isRunCharge = false) => {
        const newDecorations = [...rows];
        newDecorations[decorationIndex].rows.push({
            id: '',
            selectedSource: '', // NEW !!!!!
            chargeName: '',
            type: isRunCharge ? "breakdown" : "Fixed Charge",
            quantity: isRunCharge ? defaultDecorationQuantity : 1,
            netCost: 0,
            margin: defaultMargin,
            retail: 0,
            clientPrice: 0,
            total: 0,
            isRunCharge,
            pricingOption: 'Display to Client',
            decoratorId:null,
            chargeId:null,
            supplierId:null,
            addressId:null,
            CurrencyCode: orderItem?.CurrencyCode || order?.CurrencyCode || null,
            currencyCode: orderItem?.CurrencyCode || order?.CurrencyCode || null,
            PricingCurrencyCode: order?.CurrencyCode || orderItem?.PricingCurrencyCode || null,
            pricingCurrencyCode: order?.CurrencyCode || orderItem?.PricingCurrencyCode || null,
            ConvertedNetCost: null,
            convertedNetCost: null,
            ExchangeRate: null,
            exchangeRate: null,
            convertedCurrency: null,
            isLocked: false,
            artwork: {
                imprintType: '',
                proofRequired: '',
                charge:'',
                logoName: '',
                logoSize: '',
                logoColor: '',
                repeatLogo: '',
                supplierNotes: '',
                selectedImage: '',
            }
        });
        setRows(newDecorations);
    };

    const removeRow = (decorationIndex, rowIndex) => {
      console.log("The rows is:");
      console.log(rows);
    
      // clone the decorations shallowly
      const newDecorations = [...rows];
      const dec = newDecorations[decorationIndex];
    
      if (!dec || !Array.isArray(dec.rows)) {
        console.warn("[removeRow] no decoration/rows at", { decorationIndex, rowIndex, dec });
        return;
      }
    
      // 1) Remove the row from this decoration
      dec.rows = dec.rows.filter((_, i) => i !== rowIndex);
    
      // 2) Fix DecoratorDetails.rowBindings in-place to match
      const dd = { ...(dec.DecoratorDetails || {}) };
      const binds = Array.isArray(dd.rowBindings) ? dd.rowBindings : [];
    
      // drop the binding that pointed at this row
      const withoutThis = binds.filter(
        b => Number(b.rowIndex) !== Number(rowIndex)
      );
    
      // shift everything after this rowIndex down by 1
      dd.rowBindings = withoutThis.map(b => {
        const idx = Number(b.rowIndex);
        return idx > rowIndex ? { ...b, rowIndex: idx - 1 } : b;
      });
    
      dec.DecoratorDetails = dd;
      newDecorations[decorationIndex] = dec;
    
      console.log("New dec:");
      console.log(newDecorations);
    
      // 3) Single write
      setRows(newDecorations);
    };
    

    const copyLocation = (decorationIndex) => {
         setRows(prev => {
           const src = prev?.[decorationIndex];
           if (!src) return prev;
           // preserve bindings + artwork; assign fresh ids
           const copy = scrubDecorationForCopy(src, { keepBindings: true, keepArtwork: true });
           copy.id = ''; // new decoration instance
           // also ensure each row has a fresh id (and no shared refs)
           copy.rows = (copy.rows || []).map(r => ({ ...deepClone(r), id: '' }));
           return [...prev, copy];
         });
    };

    const closeCopyActionMenu = () =>
      setCopyActionMenu({ anchorEl: null, decorationIndex: null });

    const openCopyToProduct = (decorationIndex) => {
      closeCopyActionMenu();
      setCopyToProductDecorationIndex(decorationIndex);
      setCopyTargetProductKey("");
      setCopyTargetMode("add");
      setCopyReplaceDecorationIndex("");
      setCopyToProductOpen(true);
    };

    const closeCopyToProduct = () => {
      if (copyingToProduct) return;
      setCopyToProductOpen(false);
      setCopyToProductDecorationIndex(null);
      setCopyTargetProductKey("");
      setCopyTargetMode("add");
      setCopyReplaceDecorationIndex("");
    };

    const selectedCopyTarget = copyTargetItems.find(
      (item, index) => getCartProductCopyKey(item, index) === copyTargetProductKey
    ) || null;
    const sourceCopyDecoration =
      copyToProductDecorationIndex != null
        ? rows?.[copyToProductDecorationIndex] || null
        : null;
    const sourceCopyLocation = getDecorationLocation(sourceCopyDecoration);
    const selectedTargetDecorations = selectedCopyTarget
      ? getProductDecorations(selectedCopyTarget).map((decoration, index) => ({
          decoration,
          index,
          location: getDecorationLocation(decoration),
        }))
      : [];
    const matchingTargetDecorations = selectedCopyTarget && sourceCopyLocation
      ? findMatchingDecorationLocations(selectedCopyTarget, sourceCopyLocation)
      : [];

    const selectCopyTargetProduct = (targetKey) => {
      setCopyTargetProductKey(targetKey);
      const target = copyTargetItems.find(
        (item, index) => getCartProductCopyKey(item, index) === targetKey
      );
      const source = copyToProductDecorationIndex != null
        ? rows?.[copyToProductDecorationIndex]
        : null;
      const matches = target && source
        ? findMatchingDecorationLocations(target, getDecorationLocation(source))
        : [];

      if (matches.length) {
        setCopyTargetMode("replace");
        setCopyReplaceDecorationIndex(String(matches[0].index));
      } else {
        setCopyTargetMode("add");
        setCopyReplaceDecorationIndex("");
      }
    };

    const confirmCopyToProduct = async () => {
      if (
        !sourceCopyDecoration ||
        !selectedCopyTarget ||
        !copyTargetProductKey ||
        typeof onCopyDecorationToProduct !== "function"
      ) {
        return;
      }

      const replaceIndex =
        copyTargetMode === "replace" && copyReplaceDecorationIndex !== ""
          ? Number(copyReplaceDecorationIndex)
          : null;
      const replaceEntry =
        replaceIndex != null
          ? selectedTargetDecorations.find((entry) => entry.index === replaceIndex)
          : null;

      if (copyTargetMode === "replace" && !replaceEntry) return;

      const scrubbed = scrubDecorationForCopy(sourceCopyDecoration, {
        keepBindings: true,
        keepArtwork: true,
      });
      const targetDecorationId =
        copyTargetMode === "replace"
          ? (
              replaceEntry?.decoration?.id ??
              replaceEntry?.decoration?.DecorationId ??
              replaceEntry?.decoration?.decorationId ??
              null
            )
          : null;
      const copyPayload = buildDecorationCopyPayload(scrubbed, {
        targetDecorationId,
        targetLocation:
          copyTargetMode === "replace"
            ? replaceEntry?.location || null
            : null,
      });

      setCopyingToProduct(true);
      try {
        const result = await onCopyDecorationToProduct({
          targetProductKey: copyTargetProductKey,
          decoration: copyPayload,
          mode: copyTargetMode,
          targetDecorationIndex: replaceIndex,
        });
        if (result?.ok) {
          setCopyToProductOpen(false);
          setCopyToProductDecorationIndex(null);
          setCopyTargetProductKey("");
          setCopyTargetMode("add");
          setCopyReplaceDecorationIndex("");
        }
      } finally {
        setCopyingToProduct(false);
      }
    };

    const onSelectImage = (imgOrObj) => {
      if (activeDecorationIndex === null) return;
    
      const next = [...rows];
      const prev = next[activeDecorationIndex] || {};
      const art  = prev.artwork || {};
      const optionIndex = supportsPromoStoreArtworkChoices ? activeArtworkChoiceIndex : null;
      if (optionIndex !== null && optionIndex !== undefined) {
        const artWorks = Array.isArray(prev.ArtWorks) && prev.ArtWorks.length
          ? [...prev.ArtWorks]
          : [normalizeArtworkFields(art)];
        const current = artWorks[optionIndex] || {};
        if (typeof imgOrObj === "string") {
          artWorks[optionIndex] = { ...current, selectedImage: imgOrObj };
        } else {
          const { originalUrl, previewUrl } = imgOrObj || {};
          artWorks[optionIndex] = {
            ...current,
            selectedImage: originalUrl || current.selectedImage || null,
            previewUrl: previewUrl || current.previewUrl || null,
          };
        }
        next[activeDecorationIndex] = {
          ...prev,
          ArtWorks: artWorks,
          artwork: artWorks[0] || art,
          image: artWorks[0]?.selectedImage || prev.image || null,
        };
        setRows(next);
        clearBrokenFor(activeDecorationIndex);
        return;
      }
    
      if (typeof imgOrObj === "string") {
        next[activeDecorationIndex] = {
          ...prev,
          image: imgOrObj,
          artwork: { ...art, selectedImage: imgOrObj },
        };
      } else {
        const { originalUrl, previewUrl } = imgOrObj || {};
        next[activeDecorationIndex] = {
          ...prev,
          image: originalUrl || previewUrl || prev.image || null,
          artwork: {
            ...art,
            selectedImage: originalUrl || art.selectedImage || null,
            previewUrl: previewUrl || art.previewUrl || null,
          },
        };
      }
    
      setRows(next);
      clearBrokenFor(activeDecorationIndex);
    };
    
    

    const addNewLocation = (decorationIndex) => {
        const defaultLocation = orderItem?.Imprinting?.Locations?.Values?.[0]?.Name || ''
        const state = {
            id: "",
            Location: defaultLocation || "",
            image: null,
            DecoratorDetails: {
                promoStoreSelectionMode: isPromoStore ? "optional" : "",
                promoStoreSelectionGroup: "",
            },
            artwork: {
                imprintType: '',
                charge:'',
                proofRequired: '',
                logoName: '',
                logoSize: '',
                logoColor: '',
                repeatLogo: '',
                supplierNotes: '',
                selectedImage: '',
            },
            rows: [{
                id: 1, chargeName: '', type: "breakdown", quantity: defaultDecorationQuantity, netCost: 0, margin: defaultMargin, retail: 0, clientPrice: 0, total: 0, pricingOption: 'Display to Client', isLocked: false,
            }]

        }
        const newDecorations = [...asArray(rows), { ...state }];
        setRows(newDecorations);
        setInternationalShowCostDropdown(false)
    };

    const handleArtworkFormChange = (name, value,charge=0) => {
        if (activeDecorationIndex === null) return;
        const newDecorations = [...rows];
        const decoration = newDecorations[activeDecorationIndex];
        const optionIndex = supportsPromoStoreArtworkChoices ? activeArtworkChoiceIndex : null;
        if (optionIndex !== null && optionIndex !== undefined) {
            const artWorks = Array.isArray(decoration.ArtWorks) && decoration.ArtWorks.length
              ? [...decoration.ArtWorks]
              : [normalizeArtworkFields(decoration.artwork || {})];
            const current = artWorks[optionIndex] || normalizeArtworkFields({});
            artWorks[optionIndex] = name === 'imprintType'
              ? { ...current, charge, [name]: value }
              : { ...current, [name]: value };
            decoration.ArtWorks = artWorks;
            decoration.artwork = artWorks[0] || decoration.artwork || {};
            decoration.image = decoration.artwork?.selectedImage ?? decoration.image ?? null;
            setRows(newDecorations);
            if (name === "selectedImage") clearBrokenFor(activeDecorationIndex);
            return;
        }
        if (name === 'imprintType') {
            decoration.artwork = {
                ...decoration.artwork,
                charge:charge,
                [name]: value
            };
        } else {
            decoration.artwork = {
                ...decoration.artwork,
                [name]: value
            };
        }
        setRows(newDecorations);
        if (name === "selectedImage") {
          clearBrokenFor(activeDecorationIndex);
        }

    };
    const openArtworkChoiceEditor = (decorationIndex, artworkIndex = null) => {
        setActiveDecorationIndex(decorationIndex);
        setActiveRowIndex(null);
        setActiveArtworkChoiceIndex(artworkIndex);
        setUploadDialogOpen(true);
    };

    const addArtworkChoice = (decorationIndex) => {
      setRows((prevRows) => {
        const next = [...asArray(prevRows)];
        const decoration = next[decorationIndex];
        if (!decoration) return prevRows;
        const artWorks = Array.isArray(decoration.ArtWorks) && decoration.ArtWorks.length
          ? [...decoration.ArtWorks]
          : [normalizeArtworkFields(decoration.artwork || {})];
        artWorks.push(normalizeArtworkFields({ logoName: `Artwork ${artWorks.length + 1}` }));
        next[decorationIndex] = {
          ...decoration,
          ArtWorks: artWorks,
          artwork: artWorks[0] || decoration.artwork || {},
        };
        return next;
      });
      openArtworkChoiceEditor(decorationIndex, (rows?.[decorationIndex]?.ArtWorks?.length || 1));
    };

    const removeArtworkChoice = (decorationIndex, artworkIndex) => {
      setRows((prevRows) => {
        const next = [...asArray(prevRows)];
        const decoration = next[decorationIndex];
        if (!decoration) return prevRows;
        const artWorks = (Array.isArray(decoration.ArtWorks) ? decoration.ArtWorks : [])
          .filter((_, index) => index !== artworkIndex);
        next[decorationIndex] = {
          ...decoration,
          ArtWorks: artWorks,
          artwork: artWorks[0] || normalizeArtworkFields({}),
          image: artWorks[0]?.selectedImage ?? null,
        };
        return next;
      });
    };

    const renderArtworkPreview = (decorationIndex, artwork, artworkIndex = null, artworkCount = 1) => {
      const key = artworkIndex === null ? `dec-${decorationIndex}` : `dec-${decorationIndex}-${artworkIndex}`;
      const preferredPreview = artwork?.previewUrl || getArtworkPreviewUrl(artwork) || "";
      const displaySrc =
        (imgBroken[key] && preferredPreview
          ? (artwork?.selectedImage || "")
          : preferredPreview) ||
        artwork?.selectedImage ||
        "";

      const openHref = artwork?.selectedImage || displaySrc;
      const hasDisplay = !!displaySrc;
      const ext = getExt(displaySrc).toLowerCase();
      const isImg = IMG_EXT_RX.test(displaySrc) && !(imgBroken[key] && displaySrc === preferredPreview);
      const isPdf = ext === "pdf";
      const canRemoveArtwork =
        supportsPromoStoreArtworkChoices &&
        artworkIndex !== null &&
        artworkCount > 1;

      const openEditor = () => openArtworkChoiceEditor(decorationIndex, artworkIndex);

      if (!hasDisplay) {
        return (
          <Box
            className={styles.dottedBox}
            onClick={openEditor}
            data-testid={supportsPromoStoreArtworkChoices ? "promostore-artwork-preview" : undefined}
          >
            + Add Artwork
          </Box>
        );
      }

      const wrapperProps = {
        className: styles.imagePreview,
        "data-testid": supportsPromoStoreArtworkChoices ? "promostore-artwork-preview" : undefined,
        sx: {
          border: "1px dashed #bdbdbd",
          borderRadius: "8px",
          width: 132,
          height: 132,
          overflow: "hidden",
          cursor: "pointer",
          bgcolor: "#fafafa",
          position: "relative",
          flex: "0 0 auto",
          "&:hover .promostore-artwork-remove": {
            opacity: 1,
          },
        },
        onClick: openEditor,
      };

      return (
        <Tooltip title={`${getFileName(openHref)} • ${getExt(openHref)}`}>
          <Box {...wrapperProps}>
            <IconButton
              size="small"
              aria-label="Preview file"
              title="Preview"
              onClick={(e) => {
                e.stopPropagation();
                window.open(openHref, "_blank", "noopener,noreferrer");
              }}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                zIndex: 2,
                bgcolor: "white",
                boxShadow: 1,
                "&:hover": { bgcolor: "white" },
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>

            {canRemoveArtwork && (
              <IconButton
                size="small"
                aria-label="Remove artwork"
                title="Remove artwork"
                className="promostore-artwork-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  removeArtworkChoice(decorationIndex, artworkIndex);
                }}
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 6,
                  zIndex: 2,
                  bgcolor: "white",
                  boxShadow: 1,
                  opacity: 0,
                  transition: "opacity 120ms ease-in-out",
                  "&:hover": { bgcolor: "white" },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}

            {isImg ? (
              <img
                src={displaySrc}
                alt="Artwork"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                onError={() => setImgBroken((m) => ({ ...m, [key]: true }))}
                loading="eager"
                decoding="async"
              />
            ) : isPdf ? (
              <PdfThumb src={displaySrc} width={132} height={132} />
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  pointerEvents: "none",
                  gap: 0.5,
                }}
              >
                <InsertDriveFileOutlinedIcon />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {getExt(openHref)}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
      );
    };

    const iconClick = (decorationIndex, rowIndex) => {
        setSelectedRowInfo({ decorationIndex, rowIndex });
        setDecorationPopupOpen(true);
    }
    // Tracks the source for each row decoration index
    const handleSourceChange = (value, index) => {
        const newDecorations = [...rows];
        newDecorations[index].selectedSource = value;
        setRows(newDecorations);
    };

    const DEPARTMENTS = {
      softgoods: { id: 1, label: 'Elite Decoration Dept (Soft Goods)' },
      art:       { id: 2, label: 'Elite Art Department' },
      awards:    { id: 4, label: 'Elite Awards Department' }, // you used 4 for Awards
    };

    // Optional: if you want to normalize "source" too
    const SOURCE_BY_ID = { 1: 'softgoods', 2: 'art', 4: 'awards' };

    // Given a decorator row from your DB (id + title), make the decoration consistent
    const onDecoratorPicked = (decorationIndex, { id: newId, title: newLabel, rowIndexScope = null, CurrencyCode, currencyCode, supplierId, addressId, decorator } = {}) => {
      setRows(prev => {
        const base = Array.isArray(prev) ? prev.slice() : [];
        const curr = base[decorationIndex];
        if (!curr) return base;

        const prevParentId = Number(curr?.DecoratorDetails?.decoratorId ?? 0);
        const nextParentId = Number(newId);
        const pickedCurrency = CurrencyCode ?? currencyCode ?? decorator?.CurrencyCode ?? decorator?.currencyCode ?? null;
        const pickedSupplierId = supplierId ?? decorator?.supplierId ?? decorator?.SupplierId ?? null;
        const pickedAddressId = addressId ?? decorator?.addressId ?? decorator?.AddressId ?? null;
        const pricingCurrency = order?.CurrencyCode || orderItem?.PricingCurrencyCode || null;

           // Row-scoped update: only change that one row and leave others alone
           if (rowIndexScope != null && Array.isArray(curr.rows) && curr.rows[rowIndexScope]) {
             const rows = curr.rows.map((r, i) => i === rowIndexScope ? {
               ...r,
               decoratorId: nextParentId,
               // drop stale link just for this row
               chargeId: null,
               chargeName: '',
               netCost: 0,
               retail: 0,
               clientPrice: 0,
               total: 0,
               CurrencyCode: pickedCurrency || r.CurrencyCode || r.currencyCode || null,
               currencyCode: pickedCurrency || r.currencyCode || r.CurrencyCode || null,
               PricingCurrencyCode: pricingCurrency || r.PricingCurrencyCode || r.pricingCurrencyCode || null,
               pricingCurrencyCode: pricingCurrency || r.pricingCurrencyCode || r.PricingCurrencyCode || null,
               supplierId: pickedSupplierId ?? r.supplierId ?? null,
               addressId: pickedAddressId ?? r.addressId ?? null,
               ConvertedNetCost: null,
               convertedNetCost: null,
               ExchangeRate: null,
               exchangeRate: null,
               convertedCurrency: null,
             } : r);
        
             base[decorationIndex] = {
               ...curr,
               DecoratorDetails: {
                 ...(curr.DecoratorDetails || {}),
                 decoratorId: nextParentId,
                 methodName: newLabel || curr?.DecoratorDetails?.methodName || '',
               },
               rows,
             };
             return base;
           }

        // Always stamp parent; only clear rows if dept actually changed
        const nextDD = {
          ...(curr.DecoratorDetails || {}),
          decoratorId: nextParentId,
          methodName: newLabel || curr?.DecoratorDetails?.methodName || '',
          chargeId: null,                                      // drop stale link
          source: SOURCE_BY_ID[nextParentId] ?? curr?.DecoratorDetails?.source,
        };

        let nextRows = curr.rows;
        if (Array.isArray(curr.rows) && prevParentId !== nextParentId) {
          nextRows = curr.rows.map(r => ({
            ...r,
            decoratorId: nextParentId,
            chargeId: null,
            chargeName: '',                                    // no more “Embroidery”
            netCost: 0,
            retail: 0,
            clientPrice: 0,
            total: 0,
            CurrencyCode: pickedCurrency || r.CurrencyCode || r.currencyCode || null,
            currencyCode: pickedCurrency || r.currencyCode || r.CurrencyCode || null,
            PricingCurrencyCode: pricingCurrency || r.PricingCurrencyCode || r.pricingCurrencyCode || null,
            pricingCurrencyCode: pricingCurrency || r.pricingCurrencyCode || r.PricingCurrencyCode || null,
            supplierId: pickedSupplierId ?? r.supplierId ?? null,
            addressId: pickedAddressId ?? r.addressId ?? null,
            ConvertedNetCost: null,
            convertedNetCost: null,
            ExchangeRate: null,
            exchangeRate: null,
            convertedCurrency: null,
          }));
        } else if (Array.isArray(curr.rows)) {
          // Ensure row decoratorId matches parent, but don't wipe pricing if dept same
          nextRows = curr.rows.map(r => ({ ...r, decoratorId: nextParentId }));
        }

        // Optional: nudge visuals so preview doesn’t imply embroidery when dept ≠ softgoods
        const nextArtwork =
          nextParentId === 1
            ? (curr.artwork || {})
            : { ...(curr.artwork || {}), imprintType: '', charge: 0 };

        base[decorationIndex] = {
          ...curr,
          DecoratorDetails: nextDD,
          rows: nextRows,
          artwork: nextArtwork,
          image: nextArtwork?.selectedImage ?? curr.image ?? null,
        };
        return base;
      });
    };

    

    return (
      <div className="decoration-container">
        {asArray(rows).map((decoration, decorationIndex) => {
             const selectedSource = decoration.DecoratorDetails?.source ?? "";
             // look up the human‑readable label
             // ASI can return uppercase method codes while saved sources are lowercase.
             const option = sourceOptions.find(o =>
               String(o.value ?? "").toLowerCase() === String(selectedSource).toLowerCase()
             );
             const selectValue = option?.value ?? selectedSource;
             const methodName = option?.label || decoration.DecoratorDetails?.methodName || selectedSource;
             const source = decoration.DecoratorDetails?.source ?? "";
             const isElite = source === "elite";
             const isCustomSupplier = source === "supplier-custom";
             const isThirdParty = source.startsWith("thirdparty|");
             const { imprintLocations, imprintSizes, imprintColors } = getOptionsFromSource(source);
             const [sourceType, methodCode] = (decoration.DecoratorDetails?.source || "").split("|");  
             // lookup the ASI method object so you can grab its human name
             const methodObj = imprintMethods.find(m => m.Code === methodCode) || {};  
             const methodLabel = methodObj.Name || "";  
             return (
               <Box className="edit-product-decoration-source" key={decoration.id || `dec-${decorationIndex}`}>
                 {
                    // JSON.stringify(decoration)
                 }
                 <Box className="decoration-row-header">
                     <Typography variant="subtitle2" sx={{ color: "#555454", mb: 0.5 }}>
                     ({decorationIndex + 1}) - Decoration source for imprinting 
                     </Typography>
                     
                     <Box sx={{ position: "absolute", top: "15px", right: "15px", display: 'flex', alignItems: 'center', gap: 1 }}>
                         <Tooltip title="Clear this decoration (keep instance)">
                         <IconButton
                             size="small"
                             onClick={() => requestConfirmDecoration('clear', decorationIndex)}
                         >
                             <CleaningServicesIcon fontSize="small" />
                         </IconButton>
                         </Tooltip>
                         <Tooltip title="Delete this decoration (remove instance)">
                         <IconButton
                             size="small"
                             color="error"
                             onClick={() => requestConfirmDecoration('delete', decorationIndex)}
                         >
                             <DeleteOutlineIcon fontSize="small" />
                         </IconButton>
                         </Tooltip>
                     </Box>

<select
value={selectValue}
onChange={e => {
const v = e.target.value;
const next = [...rows];

const pretty = humanizeSource(v, { sourceOptions, imprintMethods });

next[decorationIndex].DecoratorDetails = {
...(next[decorationIndex].DecoratorDetails || {}),
source: v,
methodName: pretty,     // ← store the label, not the code
chargeId: null,         // drop stale binding on source change
// The selected source, not the previous decoration, owns this classification.
...(v === "supplier-custom" ? {
  decoratorId: null,
  isEliteDecorator: false,
  productionDepartmentId: null,
  ProductionDepartmentId: null,
  rowBindings: [],
  RowBindings: [],
} : {}),
...(v === "elite" ? { isEliteDecorator: true } : {}),
};

if (v === "supplier-custom" && Array.isArray(next[decorationIndex].rows)) {
  next[decorationIndex].rows = next[decorationIndex].rows.map(r => ({
    ...r,
    decoratorId: null,
    productionDepartmentId: null,
    chargeId: null,
  }));
}

// If 3rd-party, also stamp decoratorId and clear stale row charge links
if (v.startsWith("thirdparty|")) {
const [, idStr] = v.split("|");
const decId = Number(idStr) || null;
next[decorationIndex].DecoratorDetails.decoratorId = decId;
if (Array.isArray(next[decorationIndex].rows)) {
 next[decorationIndex].rows = next[decorationIndex].rows.map(r => ({
   ...r,
   decoratorId: decId,
   chargeId: null,
   PricingCurrencyCode: order?.CurrencyCode || r.PricingCurrencyCode || r.pricingCurrencyCode || null,
   pricingCurrencyCode: order?.CurrencyCode || r.pricingCurrencyCode || r.PricingCurrencyCode || null,
 }));
}
}

setRows(next);
}}
>
<option value="">No decoration required</option>
<option value="supplier-custom">Custom Request (Supplier)</option>

{/* Supplier general (your earlier requirement) */}

{/* Existing ASI method options */}
{sourceOptions.map(opt => (
<option key={opt.value} value={opt.value}>
{opt.label}
</option>
))}

{(isThirdParty || (selectedSource.startsWith("supplier|") && !option)) ? (
<option value={selectedSource}>
{isThirdParty ? humanizeSource(selectedSource, { sourceOptions, imprintMethods }) : methodName}
</option>
) : null}
</select>

                    {isPromoStore ? (
                      <Box sx={{ mt: 1.25, display: "grid", gap: 1, maxWidth: 360 }}>
                        <Box>
                          <Typography variant="caption" sx={{ display: "block", color: "#5f6368", mb: 0.5 }}>
                            PromoStore Behavior
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", color: "#8a8f98", mb: 0.75 }}>
                            Applies to every sale automatically uses this decoration and its artwork for each purchased product. Charge pricing is configured separately below.
                          </Typography>
                          <select
                            aria-label={`PromoStore Behavior ${decorationIndex + 1}`}
                            value={normalizePromoStoreSelectionMode(decoration.DecoratorDetails?.promoStoreSelectionMode)}
                            onChange={(event) => {
                              const mode = normalizePromoStoreSelectionMode(event.target.value);
                              setRows((previous) => previous.map((entry, index) => {
                                if (index !== decorationIndex) return entry;
                                return {
                                  ...entry,
                                  DecoratorDetails: {
                                    ...(entry.DecoratorDetails || {}),
                                    promoStoreSelectionMode: mode,
                                    promoStoreSelectionGroup: requiresPromoStoreSelectionGroup(mode)
                                      ? (entry.DecoratorDetails?.promoStoreSelectionGroup || "")
                                      : "",
                                  },
                                };
                              }));
                            }}
                          >
                            <option value="">Select PromoStore behavior...</option>
                            {PROMOSTORE_SELECTION_MODES.map((selectionMode) => (
                              <option key={selectionMode.id} value={selectionMode.id}>
                                {selectionMode.name}
                              </option>
                            ))}
                          </select>
                        </Box>

                        {requiresPromoStoreSelectionGroup(decoration.DecoratorDetails?.promoStoreSelectionMode) ? (
                          <CustomTextField
                            name="promoStoreSelectionGroup"
                            label="PromoStore Selection Group"
                            placeholder="e.g. Front Logo"
                            value={decoration.DecoratorDetails?.promoStoreSelectionGroup || ""}
                            handleChange={(event) => {
                              const value = event.target.value;
                              setRows((previous) => previous.map((entry, index) => (
                                index !== decorationIndex ? entry : {
                                  ...entry,
                                  DecoratorDetails: {
                                    ...(entry.DecoratorDetails || {}),
                                    promoStoreSelectionGroup: value,
                                  },
                                }
                              )));
                            }}
                          />
                        ) : null}
                      </Box>
                    ) : null}

                 </Box>
                 {decoration.DecoratorDetails?.source ? (
                     <Box className="decoration-row">

                         {/* Location and Image Section */}
                         <Box className={styles.locationImageContainer}>
                             <Box className={styles.field}>
                                 <CustomTextField
                                     name="Location"
                                     placeholder="Enter Location"
                                     label={"Location"}
                                     value={decoration.Location}
                                     // iconClick={iconClick}
                                     handleChange={(e, name) => {
                                         const newDecorations = [...rows];
                                         newDecorations[decorationIndex].Location = e.target.value;
                                         setRows(newDecorations);
                                     }}
                                 />
                                   {(!isElite && !isCustomSupplier && !isThirdParty) ? 
                                     <Box
                                         sx={{
                                             mb: 1,
                                             display: 'flex',
                                             flexWrap: 'wrap',
                                             gap: 1,
                                             alignItems: 'center',
                                         }}
                                         >
                                         <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                             Supplier offers locations for {methodName || "Elite Decorations" }
                                         </Typography>
                                         {imprintLocations.map(loc => (
                                             <Chip
                                             key={loc.value}
                                             label={loc.label}
                                             size="small"
                                             sx={{
                                                 backgroundColor: 'darkcyan',  // or '#006d6d' etc.
                                                 color: '#FFF',
                                                 fontWeight: '500',
                                                 borderRadius: '8px',
                                                 // if you want a bit of elevation:
                                                 boxShadow: 1,
                                             }}
                                             />
                                         ))}
                                     </Box>
                                 : " " }
                             </Box>
                             <Box
                               className={styles.imageContainer}
                               sx={supportsPromoStoreArtworkChoices ? {
                                 justifyContent: "flex-start",
                                 alignItems: "center",
                                 flex: "1 1 auto",
                                 minWidth: 0,
                                 maxWidth: { xs: "100%", md: "calc(100vw - 760px)" },
                                 overflowX: "auto",
                                 overflowY: "hidden",
                               } : undefined}
                             >
                               {(() => {
                                 const artworkChoices = getDecorationArtworkChoices(decoration, supportsPromoStoreArtworkChoices);

                                 if (!supportsPromoStoreArtworkChoices) {
                                   return renderArtworkPreview(decorationIndex, artworkChoices[0], null, 1);
                                 }

                                 return (
                                   <Box
                                     data-testid="promostore-artwork-choice-row"
                                     sx={{
                                       display: "flex",
                                       alignItems: "center",
                                       gap: 1,
                                       flexWrap: "nowrap",
                                       flex: "0 0 auto",
                                     }}
                                   >
                                     {artworkChoices.map((artwork, artworkIndex) => (
                                       <Box
                                         key={artwork?.id || artwork?.selectedImage || artwork?.previewUrl || artworkIndex}
                                         sx={{ flex: "0 0 auto" }}
                                       >
                                         {renderArtworkPreview(decorationIndex, artwork, artworkIndex, artworkChoices.length)}
                                       </Box>
                                     ))}
                                     {shouldShowInlineArtworkAddControl(supportsPromoStoreArtworkChoices) && (
                                       <Box
                                         onClick={() => addArtworkChoice(decorationIndex)}
                                         data-testid="promostore-artwork-add-inline"
                                         sx={{
                                           width: 132,
                                           height: 132,
                                           border: "1px dashed #9e9e9e",
                                           borderRadius: "8px",
                                           display: "flex",
                                           alignItems: "center",
                                           justifyContent: "center",
                                           cursor: "pointer",
                                           color: "primary.main",
                                           fontWeight: 700,
                                           textAlign: "center",
                                           px: 1,
                                           flex: "0 0 auto",
                                           bgcolor: "#fff",
                                         }}
                                       >
                                         + Add Artwork
                                       </Box>
                                     )}
                                   </Box>
                                 );
                               })()}
                             </Box>
                             <Box
                               className={styles.proofRequirementNotice}
                               data-testid="decoration-proof-status"
                               sx={supportsPromoStoreArtworkChoices ? { ml: "auto", flexShrink: 0, alignSelf: "center" } : undefined}
                             >
                                 {(() => {
                                     // find the option whose id matches the stored proofRequired
                                     //alert(decoration.artwork.proofRequired)
                                     const opt = proofRequiredOptions.find(o =>
                                         o.id === parseInt(decoration?.artwork?.proofRequiredAction || [])
                                     );
                                     
                                     if (opt) {
                                     return (
                                         <Typography className="proof-notice-text proof-status-set">
                                             <strong>Proof Status:</strong> {opt.name}
                                         </Typography>
                                     );
                                     } else {
                                     return (
                                         <Typography className="proof-notice-text">
                                             <strong>No proof status set</strong>
                                         </Typography>
                                     );
                                     }
                                 })()}
                             </Box>
                         </Box>

                         {/* Table */}
                         <Table className={styles.table}>
                             <TableHead>
                                 <TableRow className={styles.tableHeader}>
                                     <TableCell className={styles.tableHeaderCell} colSpan={3}></TableCell>
                                     <TableCell className={styles.tableHeaderCell}></TableCell>
                                     <TableCell className={styles.tableHeaderCell}></TableCell>
                                     <TableCell className={styles.tableHeaderCell}></TableCell>
                                     <TableCell className={styles.tableHeaderCell}></TableCell>
                                     <TableCell className={styles.tableHeaderCell}></TableCell>
                                     <TableCell className={styles.tableHeaderCell}></TableCell>
                                 </TableRow>
                             </TableHead>
                             <TableBody>
                                    {decoration.rows.length === 0 && (
                                      <TableRow>
                                        <TableCell colSpan={12}>
                                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                            No decoration charges. You can still attach artwork and send it to the supplier.
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                    {decoration.rows.map((row, rowIndex) => {
                                      const locked = showNetCostLocks && isLockedBool(row?.isLocked);
                                      const hasConvertedDecorationCost = hasConvertedCostBasis(row);
                                      const effectiveDecorationCost = getEffectiveDecorationCost(row);
                                      const liveTotal = deriveTotalFromRow(row);
                                      const chargeNameOverrideKey = `${decoration.id ?? decoration.decorationId ?? decoration.DecorationId ?? decorationIndex}:${row.id ?? row.rowId ?? rowIndex}`;
                                      const manualChargeNameOverride = manualChargeNameOverrides.has(chargeNameOverrideKey);
                                      const eliteChargeNameReadOnly = isEliteChargeNameReadOnly({
                                        source: decoration.DecoratorDetails?.source,
                                        row,
                                        decoratorDetails: decoration.DecoratorDetails,
                                        rowIndex,
                                        manualOverride: manualChargeNameOverride,
                                      });
                                      return (
                                       <TableRow 
                                        key={(row.id && `row-${row.id}`) || `${decorationIndex}-${rowIndex}-${row.chargeId ?? 'x'}`}
                                        className={styles.row}
                                        >
                                         <TableCell className={styles.iconCell}>
                                           {(() => {
                                             return (
                                               <IconButton
                                                 aria-label="Remove decoration charge"
                                                 onClick={() => removeRow(decorationIndex, rowIndex)}
                                                 className={styles.deleteIcon}
                                                 size="large">
                                                 <CloseIcon />
                                               </IconButton>
                                             );
                                           })()}
                                         </TableCell>
                                         <TableCell colSpan={2} className={styles.firstCell}>
                                             {/* <Box className="field"> */}

                                             <CustomTextField
                                                 name="chargeName"
                                                 placeholder={`Fee Name`}
                                                 value={row.chargeName ?? ""}
                                                 icon={
                                                     decoration.DecoratorDetails?.source === "elite" ? 
                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                           {eliteChargeNameReadOnly ? (
                                                             <Tooltip title="Enter charge name manually">
                                                               <EditOutlinedIcon
                                                                 aria-label="Enter charge name manually"
                                                                 onClick={() => {
                                                                   if (locked) return;
                                                                   setManualChargeNameOverrides((current) => {
                                                                     const next = new Set(current);
                                                                     next.add(chargeNameOverrideKey);
                                                                     return next;
                                                                   });
                                                                 }}
                                                                 sx={{ color: '#52606d', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.5 : 1 }}
                                                               />
                                                             </Tooltip>
                                                           ) : null}
                                                           <Tooltip title="Select from Decoration Matrix">
                                                             <GridOnIcon onClick={() => { if (!locked) iconClick(decorationIndex, rowIndex); }} sx={{ color: 'rgb(50 157 174)', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.5 : 1 }} />
                                                           </Tooltip>
                                                         </Box>
                                                     : null
                                                 }
                                                 border={true}
                                                 disabled={locked}
                                                 readOnly={eliteChargeNameReadOnly}
                                                 handleChange={(e, name) =>
                                                     handleRowChange(decorationIndex, rowIndex, "chargeName", e.target.value)
                                                 } />
                                             {/* </Box> */}
                                         </TableCell>
                                         <TableCell className={styles.tableBodyCell}>
                                             <Box className="field">
                                                 {!isFixedChargeRow(row) ? (
                                                     <CustomTextField name="quantity" placeholder="Enter Quantity" value={row.quantity} handleChange={(e, name) =>
                                                         handleRowChange(decorationIndex, rowIndex, "quantity", e.target.value)
                                                     } disabled={locked} />
                                                 ) : (
                                                     <Typography>1</Typography>
                                                 )}
                                             </Box>
                                         </TableCell>
                                         <TableCell className={styles.tableBodyCell}>
                                             <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                 {showNetCostLocks && (
                                                     <Tooltip title={locked ? "Locked: only Net Cost editable" : "Unlocked"}>
                                                         <IconButton
                                                             size="small"
                                                             onClick={() => toggleDecorationLock(decorationIndex, rowIndex)}
                                                             aria-label="Toggle decoration net cost lock"
                                                             sx={{display: "none"}}
                                                         >
                                                             {locked ? <LockOutlinedIcon fontSize="small" /> : <LockOpenOutlinedIcon fontSize="small" />}
                                                         </IconButton>
                                                     </Tooltip>
                                                 )}

                                                 <CustomTextField name="netCost"
                                                     placeholder="Enter Net Cost"
                                                     pricing={true}
                                                     value={row.netCost}
                                                     formatDisplayValue={formatMinimumTwoDecimalPlaces}
                                                     handleBlur={() => formatRow(decorationIndex, rowIndex)}
                                                     handleChange={(e, name) =>
                                                         handleRowChange(decorationIndex, rowIndex, "netCost", e.target.value)
                                                     } />
                                             </Box>
                                         </TableCell>
                                         <TableCell className={styles.tableBodyCell}>
                                             {row?.pricingOption !== 'Subtract From Margin' &&
                                                 <CustomTextField name="margin" value={row.margin}
                                                     formatDisplayValue={formatMinimumTwoDecimalPlaces}
                                                     pricing={true}
                                                     isMarginField={true}
                                                     placeholder={hasConvertedDecorationCost ? "Derived Margin %" : "Enter Margin %"}
                                                     helperText={hasConvertedDecorationCost ? `Cost basis: $${effectiveDecorationCost.toFixed(2)}` : null}
                                                     handleBlur={() => formatRow(decorationIndex, rowIndex)}
                                                     handleChange={(e, name) =>
                                                         handleRowChange(decorationIndex, rowIndex, "margin", e.target.value)
                                                     } disabled={locked || hasConvertedDecorationCost} />}
                                         </TableCell>
                                         <TableCell className={styles.tableBodyCell}>
                                             {row?.pricingOption !== 'Subtract From Margin' &&
                                                 <CustomTextField name="retail"
                                                     placeholder="Enter Retail %"
                                                     pricing={true}
                                                     value={row.retail}
                                                     formatDisplayValue={formatMinimumTwoDecimalPlaces}
                                                     handleBlur={() => formatRow(decorationIndex, rowIndex)}
                                                     handleChange={(e, name) =>
                                                         handleRowChange(decorationIndex, rowIndex, "retail", e.target.value)
                                                     } disabled={locked} />}
                                         </TableCell>
                                         <TableCell className={styles.clientPrice} colSpan={2}>
                                             {
                                                 isFixedChargeRow(row) ?
                                                     <PricingTypeSelect
                                                         disabled={locked}
                                                         value={row?.pricingOption}
                                                         options={options}
                                                         onChange={(e) =>
                                                             handleRowChange(decorationIndex, rowIndex, 'pricingOption', e.target.value)
                                                         }
                                                     /> :
                                                     <PricingTypeSelect
                                                         disabled={locked}
                                                         value={row?.pricingOption}
                                                         options={optionsForNormalCost}
                                                         onChange={(e) =>
                                                             handleRowChange(decorationIndex, rowIndex, 'pricingOption', e.target.value)
                                                         }
                                                     />
                                             }
                                         </TableCell>
                                         <TableCell className={styles.tableBodyCell} colSpan={2}>
                                             {row?.pricingOption !== 'Subtract From Margin' && (`$${liveTotal.toFixed(2)}`)}
                                         </TableCell>
                                       </TableRow>
                                     );
                                 })}
                             </TableBody>
                         </Table>
                         {isElite ? (
                           <span>
                             <strong style={{ color: "#329dae" }}>Reference Decoration Matrix</strong>
                           </span>
                         ) : isThirdParty ? (
                           <span>
                             <strong>Reference 3rd Party Pricing</strong> 
                           </span>
                         ) : isCustomSupplier ? (
                           <span>
                             <strong>Custom Supplier Pricing</strong> 
                           </span>
                         ) : (
                           <span>
                             <strong>Recommended Fee Name:</strong> {methodName}
                           </span>
                         )}
                         {/* Additional Text Options */}
                         <Box className={styles.clickableTextContainer} sx={{ mt: 2 }}>
                             <Typography className={styles.clickableText} onClick={() => addRow(decorationIndex, true)}>
                                 + Run charge for this decoration
                             </Typography>
                             <Typography className={styles.clickableText} onClick={() => addRow(decorationIndex)}>
                                 + Fixed charge for this decoration
                             </Typography>
                             <Typography
                               className={styles.clickableText}
                               onClick={(event) =>
                                 setCopyActionMenu({
                                   anchorEl: event.currentTarget,
                                   decorationIndex,
                                 })
                               }
                             >
                                 + Copy
                             </Typography>
                         </Box>

                     </Box>
                 ) : "Please select a decoraton source" }
               </Box>
             );
         })}
        <Menu
          anchorEl={copyActionMenu.anchorEl}
          open={Boolean(copyActionMenu.anchorEl)}
          onClose={closeCopyActionMenu}
        >
          <MenuItem
            onClick={() => {
              const decorationIndex = copyActionMenu.decorationIndex;
              closeCopyActionMenu();
              if (decorationIndex != null) copyLocation(decorationIndex);
            }}
          >
            Duplicate this decoration location
          </MenuItem>
          <MenuItem
            disabled={
              typeof onCopyDecorationToProduct !== "function" ||
              copyTargetItems.length === 0
            }
            onClick={() => {
              const decorationIndex = copyActionMenu.decorationIndex;
              if (decorationIndex != null) openCopyToProduct(decorationIndex);
            }}
          >
            Copy to another product in cart
          </MenuItem>
        </Menu>

        <Dialog
          open={copyToProductOpen}
          onClose={closeCopyToProduct}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Copy decoration to another product</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Copy {sourceCopyLocation ? `the “${sourceCopyLocation}” decoration` : "this decoration"} with its pricing, routing, and artwork.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Target product
            </Typography>
            <Select
              fullWidth
              displayEmpty
              value={copyTargetProductKey}
              onChange={(event) => selectCopyTargetProduct(event.target.value)}
            >
              <MenuItem value="" disabled>Select a product</MenuItem>
              {copyTargetItems.map((item, index) => {
                const key = getCartProductCopyKey(item, index);
                const label = item?.Name ?? item?.name ?? item?.ProductName ?? "Unnamed product";
                const identifier = item?.SKU ?? item?.ProductId ?? item?.productId ?? "";
                return (
                  <MenuItem key={key} value={key} disabled={item?.showcaseIdentity?.kind === 'catalogue'}>
                    {identifier ? `${label} — ${identifier}` : label}
                    {item?.showcaseIdentity?.kind === 'catalogue' ? ' — Catalogue product; choose a saved product' : ''}
                  </MenuItem>
                );
              })}
            </Select>

            {selectedCopyTarget ? (
              <Box sx={{ mt: 2 }}>
                {selectedTargetDecorations.length ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                      This product already has {selectedTargetDecorations.length === 1 ? "a decoration" : "decorations"}.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {matchingTargetDecorations.length
                        ? `A matching “${sourceCopyLocation}” location was found and is selected for replacement.`
                        : "No exact location match was found, so adding separately is selected by default."}
                    </Typography>
                    <Select
                      fullWidth
                      value={copyTargetMode}
                      onChange={(event) => {
                        const nextMode = event.target.value;
                        setCopyTargetMode(nextMode);
                        if (nextMode === "replace" && copyReplaceDecorationIndex === "") {
                          const preferred =
                            matchingTargetDecorations[0] ||
                            selectedTargetDecorations[0];
                          if (preferred) {
                            setCopyReplaceDecorationIndex(String(preferred.index));
                          }
                        }
                      }}
                    >
                      <MenuItem value="replace">Replace an existing decoration</MenuItem>
                      <MenuItem value="add">Add as a separate decoration</MenuItem>
                    </Select>

                    {copyTargetMode === "replace" ? (
                      <Select
                        fullWidth
                        sx={{ mt: 1 }}
                        value={copyReplaceDecorationIndex}
                        onChange={(event) => setCopyReplaceDecorationIndex(event.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Select decoration to replace</MenuItem>
                        {selectedTargetDecorations.map((entry, targetIndex) => (
                          <MenuItem key={entry.index} value={String(entry.index)}>
                            {entry.location || `Unnamed decoration #${targetIndex + 1}`}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : null}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    This product has no existing decorations. The copied decoration will be added as a new location.
                  </Typography>
                )}
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCopyToProduct} color="inherit" disabled={copyingToProduct}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={confirmCopyToProduct}
              disabled={
                copyingToProduct ||
                !copyTargetProductKey ||
                !sourceCopyDecoration ||
                (copyTargetMode === "replace" && copyReplaceDecorationIndex === "")
              }
            >
              {copyingToProduct ? "Copying..." : "Copy decoration"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmDeco.open}
          onClose={handleCancelConfirmDecoration}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {confirmDeco.action === 'delete' ? 'Delete decoration?' : 'Clear decoration?'}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">
              {confirmDeco.action === 'delete'
                ? 'This will remove this decoration instance entirely.'
                : 'This will reset this decoration’s fields, artwork, and rows to blank.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelConfirmDecoration} color="inherit">Cancel</Button>
            <Button
              onClick={handleConfirmDecoration}
              color={confirmDeco.action === 'delete' ? 'error' : 'primary'}
              variant="contained"
            >
              {confirmDeco.action === 'delete' ? 'Delete' : 'Clear'}
            </Button>
          </DialogActions>
        </Dialog>
        {copyDialogOpen && (
          <Dialog open onClose={() => setCopyDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Copy decorations from another product</DialogTitle>
            {
            //JSON.stringify(items)
            }
            <DialogContent dividers>
              {items
                .filter(i => !isSameCartProduct(i, currentCopyProduct))
                .map((item, itemIndex) => {
                  const itemKey = getCartProductCopyKey(item, itemIndex);
                  return (
                  <Box
                    key={itemKey}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">{item.Name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.ProductId}
                      </Typography>
                    </Box>
                    <Button
                      variant={copyFromItemId === itemKey ? "contained" : "outlined"}
                      onClick={() => setCopyFromItemId(itemKey)}
                    >
                      Select
                    </Button>
                  </Box>
                  );
                })}
              {items.filter(i => !isSameCartProduct(i, currentCopyProduct)).length === 0 && (
                <Typography color="text.secondary">
                  No other products available on this order.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCopyDialogOpen(false)} color="inherit">
                Cancel
              </Button>
              <Button
          onClick={() => {
            const src = items.find((item, itemIndex) =>
              getCartProductCopyKey(item, itemIndex) === copyFromItemId
            );
            handleCopyFromItem(src);
          }}
          variant="contained"
          disabled={!copyFromItemId}
        >
          Copy decorations 
        </Button>




            </DialogActions>
          </Dialog>
        )}
        {activeDecorationIndex !== null && (
          (() => {
            const source = rows[activeDecorationIndex]?.DecoratorDetails?.source;
            const {
              imprintLocations: rowLocations,
              imprintSizes:     rowSizes,
              imprintColors:    rowColors
            } = getOptionsFromSource(source);

            

            return (
                <>
                    {
                        //JSON.stringify(rowSizes)
                    }
                    <ArtworkUploadModal
                        orderItem={orderItem}
                        open={uploadDialogOpen}
                        clientFiles={clientFiles}
                        handleDone={handleDone}
                        rows={rows[activeDecorationIndex]}
                        customerId={customerId}
                        onClose={() => {
                            setActiveDecorationIndex(null);
                            setActiveRowIndex(null);
                            setActiveArtworkChoiceIndex(null);
                            setUploadDialogOpen(false)
                        }}
                        formData={
                            activeDecorationIndex !== null && activeArtworkChoiceIndex !== null
                                ? rows[activeDecorationIndex]?.ArtWorks?.[activeArtworkChoiceIndex] || {}
                                : activeDecorationIndex !== null && rows[activeDecorationIndex]?.artwork
                                  ? rows[activeDecorationIndex].artwork
                                : {}
                        }
                        onFormChange={handleArtworkFormChange}
                        onSelectImage={onSelectImage}
                        onUpload={(payload) => {
                          const newDecorations = [...rows];
                        
                          if (activeDecorationIndex !== null) {
                            const art = newDecorations[activeDecorationIndex].artwork || {};
                            const optionIndex = supportsPromoStoreArtworkChoices ? activeArtworkChoiceIndex : null;
                        
                            if (typeof payload === "string") {
                              if (optionIndex !== null && optionIndex !== undefined) {
                                const artWorks = Array.isArray(newDecorations[activeDecorationIndex].ArtWorks) && newDecorations[activeDecorationIndex].ArtWorks.length
                                  ? [...newDecorations[activeDecorationIndex].ArtWorks]
                                  : [normalizeArtworkFields(art)];
                                artWorks[optionIndex] = {
                                  ...(artWorks[optionIndex] || {}),
                                  selectedImage: payload,
                                };
                                newDecorations[activeDecorationIndex].ArtWorks = artWorks;
                                newDecorations[activeDecorationIndex].artwork = artWorks[0] || art;
                                newDecorations[activeDecorationIndex].image = newDecorations[activeDecorationIndex].artwork?.selectedImage || null;
                              } else {
                                // backward compat (old single URL)
                                newDecorations[activeDecorationIndex].image = payload;
                                newDecorations[activeDecorationIndex].artwork = {
                                  ...art,
                                  selectedImage: payload,
                                };
                              }
                            } else {
                              const { originalUrl, previewUrl, customerFileId } = payload || {};
                              if (optionIndex !== null && optionIndex !== undefined) {
                                const artWorks = Array.isArray(newDecorations[activeDecorationIndex].ArtWorks) && newDecorations[activeDecorationIndex].ArtWorks.length
                                  ? [...newDecorations[activeDecorationIndex].ArtWorks]
                                  : [normalizeArtworkFields(art)];
                                const current = artWorks[optionIndex] || {};
                                artWorks[optionIndex] = {
                                  ...current,
                                  selectedImage: originalUrl || current.selectedImage || null,
                                  previewUrl: previewUrl || current.previewUrl || null,
                                  customerFileId:
                                    customerFileId ??
                                    current.customerFileId ??
                                    null,
                                };
                                newDecorations[activeDecorationIndex].ArtWorks = artWorks;
                                newDecorations[activeDecorationIndex].artwork = artWorks[0] || art;
                                newDecorations[activeDecorationIndex].image = newDecorations[activeDecorationIndex].artwork?.selectedImage || null;
                              } else {
                                // small tile shows preview if available; eye button opens original
                                newDecorations[activeDecorationIndex].image =
                                  originalUrl || previewUrl || art.selectedImage || null;

                                newDecorations[activeDecorationIndex].artwork = {
                                  ...art,
                                  selectedImage: originalUrl || art.selectedImage || null,
                                  previewUrl: previewUrl || null,
                                  customerFileId:
                                    customerFileId ??
                                    art.customerFileId ??
                                    null,
                                };
                              }
                            }
                        
                            if (activeRowIndex !== null) {
                              const row = newDecorations[activeDecorationIndex].rows[activeRowIndex];
                              if (row) {
                                row.artwork = {
                                  ...(row.artwork || {}),
                                  selectedImage:
                                    (payload && typeof payload !== "string" ? payload.originalUrl : payload) ||
                                    row.artwork?.selectedImage ||
                                    "",
                                  previewUrl:
                                    (payload && typeof payload !== "string" ? payload.previewUrl : null) ||
                                    row.artwork?.previewUrl ||
                                    null,
                                  customerFileId:
                                    (payload && typeof payload !== "string" ? payload.customerFileId : null) ??
                                    row.artwork?.customerFileId ??
                                    null,
                                };
                              }
                            }
                        
                            setRows(newDecorations);
                          }
                        
                        }}
                        
                        decorationSource={decorationSource}
                        imprintSizes={rowSizes}
                        imprintColors={rowColors}
                        imprintLocations={rowLocations}
                        sourceOptions={sourceOptions}
                    />
                </>
            );
          })()
        )}
        <DecorationSelectorPopup
          open={decorationPopupOpen}
          onClose={() => setDecorationPopupOpen(false)}
          handleRowChange={handleRowChange}
          selectedRowInfo={selectedRowInfo}
          selectedRowData={
            selectedRowInfo.decorationIndex !== null && selectedRowInfo.rowIndex !== null
              ? (() => {
                  const dec = rows[selectedRowInfo.decorationIndex];
                  const row = dec?.rows?.[selectedRowInfo.rowIndex] ?? null;
                  const bind = dec?.DecoratorDetails?.rowBindings?.find(
                    b => Number(b.rowIndex) === Number(selectedRowInfo.rowIndex)
                  ) || null;
                  // Only use a binding if it still belongs to this same charge row.
                  return resolvePopupRowData(row, bind);
                })()
              : null
          }
          onDecoratorPicked={onDecoratorPicked}
          //syncBinding={syncBinding}
        />
        <Box className={styles.relativeWrapper}>
            <Typography className={styles.clickableText} ref={decorationRef} onClick={() => setInternationalShowCostDropdown((prev) => !prev)}
            >
                + Decoration Location
            </Typography>
            {showInternationalCostDropdown && (
                <Box className={styles.dropdownContainer}>
                    <Box className={styles.dropdownOption} onClick={() => addNewLocation(0)}>
                        Add New Decoration Location
                    </Box>
                    <Box
                        className={styles.dropdownOption}
                        onClick={() => {
                        setInternationalShowCostDropdown(false);
                        setCopyDialogOpen(true);
                        }}
                    >
                        Copy from product on this order
                    </Box>

                    {/*<Box className={styles.dropdownOption}
                    // onClick={() => addChargeRow("Brokerage")}
                    >
                        Copy From Another Product
                    </Box>*/}
                </Box>
            )}
        </Box>
      </div>
    );
};

export default Decoration;
